import { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { ShoppingCart, Plus, Minus, Trash2, CreditCard, Banknote, UtensilsCrossed, Truck, X, Check, Calculator, Receipt, Clock, AlertCircle, Play, ShoppingBag, Percent, Lock, Search, Printer, ChefHat, Edit2, RefreshCw, Edit, ChevronUp, History, Eye, Calendar, Undo, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AdminLayout } from "@/components/layouts/AdminLayout";
import { toast } from "sonner";
import axios from "axios";
import API_URL from "@/utils/api";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { offlineDB } from "@/utils/offlineDB";
import { OnlineStatusIndicator } from "@/components/OnlineStatusIndicator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import printService from "@/services/PrintService";
// printProvider/bridge removed — impresión gestionada por agente local (zt_print_agent.py)
import PrinterSettings from "@/components/PrinterSettings";
import PrintQueueWorker from "@/components/admin/PrintQueueWorker";
import { MapPin } from "lucide-react";


const API = API_URL;

// Debug: Log environment info
if (typeof window !== 'undefined') {
  console.log('[AdminPOS] API URL:', API);
}

export default function AdminPOS() {
  const { user } = useAuth();
  const location = useLocation();
  // Offline mode support
  const isOnline = useOnlineStatus();

  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [cart, setCart] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // View mode: "new" for new orders, "pending" for pending payments
  const [viewMode, setViewMode] = useState("new");

  // Pending orders (orders with payment_status = pending)
  const [pendingOrders, setPendingOrders] = useState([]);

  // Order settings
  const [orderType, setOrderType] = useState("local"); // local, delivery, or pickup
  const [tableNumber, setTableNumber] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [orderNotes, setOrderNotes] = useState("");

  // Payment dialog
  const [showPayment, setShowPayment] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [cashReceived, setCashReceived] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSearchingCustomer, setIsSearchingCustomer] = useState(false);
  const [isFrequentCustomer, setIsFrequentCustomer] = useState(false);
  const [customerDataConfirmed, setCustomerDataConfirmed] = useState(false); // New: Collapse inputs

  // Discount with admin auth
  const [showDiscountDialog, setShowDiscountDialog] = useState(false);
  const [discountPercent, setDiscountPercent] = useState("");
  const [discountReason, setDiscountReason] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [isVerifyingDiscount, setIsVerifyingDiscount] = useState(false);

  const [paymentNote, setPaymentNote] = useState(""); // Change calculation state

  // Selected pending order for payment
  const [selectedPendingOrder, setSelectedPendingOrder] = useState(null);
  const [showPendingPayment, setShowPendingPayment] = useState(false);
  const [pendingPaymentMethod, setPendingPaymentMethod] = useState("cash");

  // Filter for pending orders
  const [pendingFilter, setPendingFilter] = useState("all"); // all, local, delivery, pickup
  const [searchQuery, setSearchQuery] = useState(""); // Search in pending orders

  // Menu Selection Dialog
  const [menuSelectionItem, setMenuSelectionItem] = useState(null);
  const [selectedItemExtras, setSelectedItemExtras] = useState([]); // New: Track extras for burger/kebab
  const [sizeSelectionItem, setSizeSelectionItem] = useState(null);
  // Gratinado options within the size dialog
  const [sizeDialogGratinado, setSizeDialogGratinado] = useState(false);
  const [sizeDialogGratinadoQueso, setSizeDialogGratinadoQueso] = useState('Mozzarella');
  const [sizeDialogDough, setSizeDialogDough] = useState(null); // New: Dough selection
  const [showTacoBuilder, setShowTacoBuilder] = useState(false);
  const [tacoConfig, setTacoConfig] = useState({
    size: 'M',
    sauces: [],
    meats: [],
    extras: [],
    isGratinado: false,
    gratinadoQueso: 'Mozzarella',
    dough: null, // New: Dough
    isMenu: false
  });

  const TACO_OPTIONS = {
    sizes: [
      { id: 'M', name: 'M', price: 5.0 },
      { id: 'L', name: 'L', price: 8.0 },
      { id: 'XL', name: 'XL', price: 11.0 },
      { id: 'XXL', name: 'XXL', price: 14.0 }
    ],
    sauces: [
      'Cheddar', 'Z y R', 'Ketchup', 'Mayonesa', 'Mostaza', 'Yogur', 'Samuray', 'Andaluza', 'Algerina', 'BBQ', 'Harisa'
    ],
    meats: [
      'Pollo', 'Pinchos Moruno', 'Pollo Crispy', 'Nugget', 'Ternera Picada', 'Ternera Aliñada', 'Falafel', 'Kebab Pollo', 'Bacon'
    ],
    extras: [
      'Doble Carne', 'Extra Salsa', 'Extra Queso', 'Mozzarella', 'Q. Cheddar', 'Q. Cabra', 'Aceitunas', 'Cebolla Caram.', 'Jalapeño', 'Tomate', 'Cebolla Cruj.', 'Lechuga', 'Huevo'
    ],
    gratinados: ['Cheddar', 'Cabra', 'Curado', 'Mozzarella'],
    doughs: ['Cheddar', 'Cabra', 'Curado', 'Mozzarella'] // Using names from photo as requested
  };

  // Active shift state
  const [activeShift, setActiveShift] = useState(null);
  const [checkingShift, setCheckingShift] = useState(true);

  // Edit Mode state
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [originalOrder, setOriginalOrder] = useState(null);

  // Branch support
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("all");

  // Printer status state
  const [printerStatus, setPrinterStatus] = useState("checking"); // "checking", "connected", "disconnected"
  const [showPrintSettings, setShowPrintSettings] = useState(false);

  // Print Queue State
  const [printQueueStatus, setPrintQueueStatus] = useState({ count: 0, processing: false });
  // Device Role: "caja" (main TPV) or "tablet" (ordering only)
  const [deviceRole, setDeviceRole] = useState(() => {
    // ALWAYS check saved first
    const saved = localStorage.getItem('zt_device_role');
    if (saved) return saved;

    // Simple default: PCs are Caja, Mobiles are Tablet
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    return isMobile ? 'tablet' : 'caja';
  });

  const isTPV = deviceRole === 'caja';
  const isTablet = deviceRole === 'tablet';

  // Dynamic Custom Taco Config
  const [customTacoConfig, setCustomTacoConfig] = useState(null);

  // El agente local (zt_print_agent.py) gestiona la impresión automáticamente.
  // El TPV solo necesita saber si es el PC principal para mostrar estados correctos.
  // No se necesita verificar el bridge desde el frontend.
  useEffect(() => {
    if (isTPV) {
      setPrinterStatus("connected"); // El agente Python gestiona la impresión
    }
  }, [isTPV]);


  // History state
  const [historyOrders, setHistoryOrders] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyFilters, setHistoryFilters] = useState({
    preset: 'last7',
    status: 'all',
    type: 'all',
    table: '',
    q: '',
    offset: 0,
    limit: 10
  });
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [selectedHistoryOrder, setSelectedHistoryOrder] = useState(null);
  const [showHistoryDetail, setShowHistoryDetail] = useState(false);



  const fetchActiveShift = useCallback(async () => {
    try {
      const branchParam = selectedBranch && selectedBranch !== "all" ? `?branch_id=${selectedBranch}` : "";
      const response = await axios.get(`${API}/admin/cashier-shifts/active${branchParam}`, { withCredentials: true });
      if (response.data.has_active_shift) {
        setActiveShift(response.data.shift);
        // Sync branch if not set
        if (selectedBranch === "all" && response.data.shift.branch_id) {
          setSelectedBranch(response.data.shift.branch_id);
        }
      } else {
        setActiveShift(null);
      }
    } catch (error) {
      console.error("Error fetching active shift:", error);
      setActiveShift(null);
    } finally {
      setCheckingShift(false);
    }
  }, [selectedBranch]);

  const fetchHistory = useCallback(async () => {
    setIsLoadingHistory(true);
    try {
      const { preset, status, type, table, q, offset, limit } = historyFilters;
      const params = new URLSearchParams({
        preset,
        status,
        order_type: type,
        table_number: table,
        q,
        limit,
        offset,
        branch_id: selectedBranch
      });
      const res = await axios.get(`${API}/admin/orders/history?${params.toString()}`, { withCredentials: true });
      setHistoryOrders(res.data.orders);
      setHistoryTotal(res.data.total);
    } catch (error) {
      console.error("Error fetching history:", error);
      toast.error("Error al cargar historial");
    } finally {
      setIsLoadingHistory(false);
    }
  }, [historyFilters, selectedBranch]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('view') === 'history') {
      setViewMode('history');
    }
  }, [location.search]);

  useEffect(() => {
    if (viewMode === "history") {
      fetchHistory();
    }
  }, [viewMode, fetchHistory]);

  const handleViewHistoryDetail = async (order) => {
    try {
      // Fetch full detail including events
      const res = await axios.get(`${API}/admin/orders/${order.order_id}`, { withCredentials: true });
      setSelectedHistoryOrder(res.data);
      setShowHistoryDetail(true);
    } catch (error) {
      console.error("Error fetching order detail:", error);
      toast.error("Error al cargar detalles del pedido");
    }
  };

  const handleReprint = async (target) => {
    if (!selectedHistoryOrder) return;
    try {
      await axios.post(`${API}/admin/orders/${selectedHistoryOrder.order_id}/reprint`, { target }, { withCredentials: true });
      toast.success(`Reimpresión de ${target === 'customer' ? 'ticket' : 'comanda'} encolada`);
      // Update local state to show pending
      setSelectedHistoryOrder(prev => ({
        ...prev,
        [target === 'customer' ? 'customer_print_status' : 'kitchen_print_status']: 'pending'
      }));
    } catch (error) {
      console.error("Error en reimpresión:", error);
      toast.error("Error al solicitar reimpresión");
    }
  };

  const fetchBranches = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/public/branches`);
      setBranches(res.data);
      // Auto-select user branch
      if (user?.assigned_branches?.length > 0 && selectedBranch === "all") {
        setSelectedBranch(user.assigned_branches[0]);
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  }, [user, selectedBranch]);

  // Fetch menu data, pending orders, AND available tables
  const fetchData = useCallback(async () => {
    try {
      const branchParam = selectedBranch && selectedBranch !== "all" ? `?branch_id=${selectedBranch}` : "";
      const [catRes, menuRes, ordersRes, tablesRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/menu`),
        axios.get(`${API}/admin/orders${branchParam}`, { withCredentials: true }),
        axios.get(`${API}/tables`)
      ]);
      setCategories(catRes.data.filter(c => c.active));
      setMenuItems(menuRes.data.filter(m => m.active));

      // NEW: Fetch Custom Taco Config
      try {
        const tacoCfgRes = await axios.get(`${API}/custom-taco/config`);
        if (tacoCfgRes.data) {
          setCustomTacoConfig(tacoCfgRes.data);
        }
      } catch (tacoError) {
        console.error("Error fetching taco config:", tacoError);
      }

      // Store available tables for auto-assignment
      // We attach it to the window object or a ref if we don't want to add state, 
      // but adding a state variable is cleaner. 
      // For now, let's just stick to the simplest solution:
      // We will filter on the fly or improved: add tables to state.
      setAvailableTables(tablesRes.data || []);

      // Filter orders that are pending payment (delivered but not paid, or ready for payment)
      const pending = ordersRes.data.filter(o =>
        o.payment_status === "pending" &&
        !["cancelled"].includes(o.status)
      );
      setPendingOrders(pending);

      if (catRes.data.length > 0) {
        setCategories(cats => cats);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Add state for tables
  const [availableTables, setAvailableTables] = useState([]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  useEffect(() => {
    fetchData();
    fetchActiveShift();

    // Auto-refresh pending orders
    const interval = setInterval(() => {
      fetchData();
      fetchActiveShift();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchData, fetchActiveShift, selectedBranch, isTPV]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const table = params.get('table');
    const view = params.get('view');
    const search = params.get('search');
    const editOrder = params.get('edit_order');

    if (table) {
      setTableNumber(table);
      setOrderType("local");
      setViewMode("new");
    }
    if (view) {
      setViewMode(view);
    }
    if (search) {
      setSearchQuery(search);
    }
    if (editOrder) {
      loadOrderToEdit(editOrder);
    }
  }, [location.search]);

  const loadOrderToEdit = async (orderId) => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/orders/${orderId}`, { withCredentials: true });
      if (res.data) {
        setEditingOrderId(orderId);
        setOriginalOrder(res.data);
        setOrderType(res.data.order_type);
        setTableNumber(res.data.table_number || "");
        setCustomerName(res.data.customer_name || "");
        setCustomerPhone(res.data.customer_phone || "");
        setOrderNotes(res.data.notes || "");

        // Cargar productos al carrito
        // Fix: If order is already in kitchen/printed, assume current items are printed if specific field is missing
        const isOrderPrinted = res.data.kitchen_printed || ['preparing', 'ready', 'delivered', 'served'].includes(res.data.status);

        setCart(res.data.items.map(item => ({
          ...item,
          printed_quantity: item.printed_quantity !== undefined
            ? item.printed_quantity
            : (isOrderPrinted ? item.quantity : 0)
        })));

        setViewMode("new");
        toast.info(`Editando pedido #${orderId.slice(-6)}`, { icon: "📝" });
      }
    } catch (error) {
      console.error("Error loading order to edit:", error);
      toast.error("No se pudo cargar el pedido para editar");
    } finally {
      setIsLoading(false);
    }
  };

  const cancelEditMode = () => {
    setEditingOrderId(null);
    setOriginalOrder(null);
    clearCart();
    setTableNumber("");
    setCustomerName("");
    setCustomerPhone("");
    setOrderNotes("");
    setViewMode("pending"); // Volver a vista pendientes para cobrar
    toast.info("Modo edición cancelado");
  };

  // CRM: Search customer by phone
  useEffect(() => {
    const searchCustomer = async () => {
      if (customerPhone && customerPhone.length >= 9) {
        setIsSearchingCustomer(true);
        try {
          const res = await axios.get(`${API}/admin/customers/search/${customerPhone}`, { withCredentials: true });
          if (res.data) {
            // Fill if empty or if frequent customer detected
            if (!customerName) setCustomerName(res.data.name);
            if (!deliveryAddress) setDeliveryAddress(res.data.address);
            setIsFrequentCustomer(true);
            toast.success(`Cliente reconocido: ${res.data.name}`, { icon: "🔥", duration: 2000 });
          }
        } catch (error) {
          setIsFrequentCustomer(false);
          // 404 is normal for new customers
        } finally {
          setIsSearchingCustomer(false);
        }
      } else {
        setIsFrequentCustomer(false);
      }
    };

    const timer = setTimeout(() => {
      searchCustomer();
    }, 700); // Debounce

    return () => clearTimeout(timer);
  }, [customerPhone]);

  const handleManualCustomerSearch = async () => {
    if (!customerPhone || customerPhone.length < 9) {
      toast.info("Introduce un teléfono válido para buscar");
      return;
    }
    setIsSearchingCustomer(true);
    try {
      const res = await axios.get(`${API}/admin/customers/search/${customerPhone}`, { withCredentials: true });
      if (res.data) {
        setCustomerName(res.data.name);
        setDeliveryAddress(res.data.address);
        setIsFrequentCustomer(true);
        toast.success(`Cliente: ${res.data.name}`);
      } else {
        setIsFrequentCustomer(false);
        toast.info("No hay registros para este teléfono");
      }
    } catch (error) {
      setIsFrequentCustomer(false);
      toast.info("Nuevo cliente / Sin registro");
    } finally {
      setIsSearchingCustomer(false);
    }
  };


  // Filter items by category
  const filteredItems = selectedCategory
    ? menuItems.filter(item => item.category_id === selectedCategory)
    : menuItems;

  // Filtered pending orders with search
  let filteredPendingOrders = pendingFilter === "all"
    ? pendingOrders
    : pendingOrders.filter(o => o.order_type === pendingFilter);

  // Apply search filter
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase();
    filteredPendingOrders = filteredPendingOrders.filter(order => {
      const orderId = order.order_id.toLowerCase();
      const customerName = (order.customer_name || "").toLowerCase();
      const customerAlias = (order.customer_alias || "").toLowerCase();
      const preorderCode = (order.preorder_code || "").toLowerCase();
      const tableNumber = (order.table_number || "").toString().toLowerCase();
      const phone = (order.customer_phone || "").toLowerCase();
      const userName = (order.user_name || "").toLowerCase();

      return orderId.includes(query) ||
        customerName.includes(query) ||
        customerAlias.includes(query) ||
        preorderCode.includes(query) ||
        tableNumber.includes(query) ||
        phone.includes(query) ||
        userName.includes(query);
    });
  }

  // Base list for counting (apply search first)
  const baseForCounts = searchQuery.trim() ? pendingOrders.filter(order => {
    const query = searchQuery.toLowerCase();
    return order.order_id.toLowerCase().includes(query) ||
      (order.customer_name || "").toLowerCase().includes(query) ||
      (order.customer_alias || "").toLowerCase().includes(query) ||
      (order.preorder_code || "").toLowerCase().includes(query) ||
      (order.table_number || "").toString().toLowerCase().includes(query) ||
      (order.customer_phone || "").toLowerCase().includes(query) ||
      (order.user_name || "").toLowerCase().includes(query);
  }) : pendingOrders;

  const localCount = baseForCounts.filter(o => o.order_type === "local").length;
  const deliveryCount = baseForCounts.filter(o => o.order_type === "delivery").length;
  const pickupCount = baseForCounts.filter(o => o.order_type === "pickup").length;

  // Cart functions
  const addToCart = (item) => {
    // Detect "Crea tu Taco" — match any common naming variant
    const lname = item.name.toLowerCase();
    if (
      lname.includes("crea tu taco") ||
      lname.includes("taco personalizado") ||
      lname.includes("ztaco") ||
      lname.includes("crea tu ztaco")
    ) {
      setTacoConfig({
        size: 'M',
        sauces: [],
        meats: [],
        extras: [],
        isGratinado: false,
        gratinadoQueso: 'Mozzarella',
        isMenu: false,
        baseItem: item // Keep reference for ID and structure
      });
      setShowTacoBuilder(true);
      return;
    }

    // 1. Robust check: items that ALWAYS have Solo vs Menu + Extras, NEVER sizes (or bypass sizes)
    // This includes Burgers (ZB), Kebabs (ZK), ZTacos (ZT) and ZBox Fries (ZX)
    const isSoloMenuOnly = 
      item.category_id === 'cat_burger' || 
      item.category_id === 'cat_kebab' ||
      item.category_id === 'cat_ztacos' ||
      lname.includes('burger') || 
      lname.includes('kebab') || 
      lname.includes('zbox') || 
      lname.includes('zdurum') ||
      lname.includes('(zt') || 
      lname.includes('ztaco') || 
      lname.includes('z taco') || 
      lname.startsWith('zb') || 
      lname.startsWith('zk') || 
      lname.startsWith('zt') || 
      lname.startsWith('zx');

    if (isSoloMenuOnly) {
      setMenuSelectionItem(item);
      return;
    }

    // 2. Tacos and Entrantes (Normal ones): Always have sizes (M,L,XL,XXL or Media/Entera)
    if (item.category_id === 'cat_tacos' || item.category_id === 'cat_entrantes' || item.category_id === 'cat_ztacos') {
      if (item.menu_price && parseFloat(item.menu_price) > 0) {
        setMenuSelectionItem(item);
      } else {
        setSizeSelectionItem({ item, is_menu: false });
      }
      return;
    }

    // If item has a menu price, show selection dialog
    if (item.menu_price && parseFloat(item.menu_price) > 0) {
      setMenuSelectionItem(item);
      return;
    }

    // Standard add
    processAddToCart(item, false);
  };

  const handleSizeSelection = (sizeId, priceAdd, gratinado = false, gratinadoQueso = 'Mozzarella', dough = null) => {
    if (!sizeSelectionItem) return;
    const { item, is_menu } = sizeSelectionItem;

    const gratinadoPrice = gratinado ? (customTacoConfig?.gratinado_price || 2.0) : 0;
    const doughPrice = (sizeId === 'M' && dough) ? (customTacoConfig?.dough_price || 1.50) : 0;
    const basePrice = parseFloat(item.price || 0) + priceAdd + gratinadoPrice + doughPrice;
    const mPrice = parseFloat(item.menu_price || 0) + priceAdd + gratinadoPrice + doughPrice;
    const gratinadoNote = gratinado ? `GRATINADO: ${gratinadoQueso}` : '';
    const doughNote = dough ? `MASA: ${dough}` : '';
    const finalNotes = [item.notes, gratinadoNote, doughNote].filter(Boolean).join(', ');

    const configuredItem = {
      ...item,
      item_id: sizeId === 'M' ? item.item_id : `${item.item_id}_${sizeId}`,
      name: sizeId === 'M' ? item.name : `${item.name} (${sizeId})`,
      price: basePrice,
      menu_price: item.menu_price ? mPrice : null,
      notes: finalNotes
    };

    processAddToCart(configuredItem, is_menu);
    setSizeSelectionItem(null);
    setSizeDialogGratinado(false);
    setSizeDialogGratinadoQueso('Mozzarella');
  };

  const processAddToCart = (item, isMenu = false) => {
    const price = isMenu ? (item.menu_price || item.price) : item.price;
    const name = isMenu ? `${item.name} (MENU)` : item.name;
    const itemKey = isMenu ? `${item.item_id}_menu` : item.item_id;

    console.log('[AdminPOS] Adding to cart:', { item_id: item.item_id, name, price, is_menu: isMenu });

    setCart(prev => {
      // Use a unique key for selection (item_id + menu status)
      const existing = prev.find(i =>
        i.item_id === item.item_id && (i.is_menu === isMenu)
      );

      if (existing) {
        const updated = prev.map(i =>
          (i.item_id === item.item_id && i.is_menu === isMenu)
            ? { ...i, quantity: i.quantity + 1 }
            : i
        );
        console.log('[AdminPOS] Cart updated (increment):', updated);
        return updated;
      }

      const newCartItem = {
        ...item,
        name,
        price,
        is_menu: isMenu,
        quantity: 1,
        extras: item.extras || [],
        notes: item.notes || "" // preserve custom taco description notes
      };

      const newCart = [...prev, newCartItem];
      console.log('[AdminPOS] Cart updated (new item):', newCart);
      return newCart;
    });
  };

  const removeFromCart = (itemId, isMenu = false) => {
    setCart(prev => prev.filter(i => {
      // Prevent removing items that are already printed in kitchen (Edit Mode)
      if (editingOrderId && i.printed_quantity > 0 && i.item_id === itemId && i.is_menu === isMenu) {
        toast.error("No se puede eliminar un artículo ya enviado a cocina");
        return true; // Keep it
      }
      return !(i.item_id === itemId && i.is_menu === isMenu);
    }));
  };

  const updateQuantity = (itemId, delta, isMenu = false) => {
    setCart(prev => prev.map(item => {
      if (item.item_id === itemId && item.is_menu === isMenu) {
        const newQty = item.quantity + delta;
        // Edit Mode Protection: Cannot reduce below printed_quantity
        if (editingOrderId && item.printed_quantity > 0 && newQty < item.printed_quantity) {
          toast.error("No se puede reducir la cantidad de lo ya enviado a cocina");
          return item;
        }
        return newQty > 0 ? { ...item, quantity: newQty } : item;
      }
      return item;
    }).filter(item => item.quantity > 0));
  };

  const clearCart = () => {
    setCart([]);
    setTableNumber("");
    setCustomerName("");
    setCustomerPhone("");
    setDeliveryAddress("");
    setOrderNotes("");
    setPaymentNote("");
    setAppliedDiscount(null);
    setCustomerDataConfirmed(false);
  };

  // Calculate totals using useMemo for reactivity
  const subtotal = useMemo(() => {
    const result = cart.reduce((sum, item) => {
      const itemPrice = parseFloat(item.price) || 0;
      const itemQty = parseInt(item.quantity) || 0;
      const extrasTotal = (item.extras || []).reduce((eSum, extra) =>
        eSum + (parseFloat(extra.price) || 0), 0
      );
      return sum + ((itemPrice + extrasTotal) * itemQty);
    }, 0);
    console.log('[AdminPOS] Calculated subtotal:', result, 'from cart:', cart.length, 'items');
    return result;
  }, [cart]);

  const deliveryFee = orderType === "delivery" ? 2.50 : 0;

  // Calculate discount
  const discountAmount = useMemo(() => {
    if (!appliedDiscount) return 0;
    // Si el descuento es del 100%, se aplica a todo (incluido envío)
    if (appliedDiscount.percent === 100) {
      return subtotal + deliveryFee;
    }
    // Si no, solo se aplica a los productos (subtotal)
    return (subtotal * appliedDiscount.percent) / 100;
  }, [subtotal, appliedDiscount, deliveryFee]);

  const total = subtotal + deliveryFee - discountAmount;

  // Verify admin password for discount
  const handleVerifyDiscount = async () => {
    if (!discountPercent || parseFloat(discountPercent) <= 0) {
      toast.error("Introduce un porcentaje de descuento válido");
      return;
    }
    if (!adminPassword) {
      toast.error("Introduce la contraseña del administrador");
      return;
    }

    setIsVerifyingDiscount(true);
    try {
      const response = await axios.post(`${API}/admin/verify-admin-discount`, {
        admin_password: adminPassword,
        discount_percent: parseFloat(discountPercent),
        reason: discountReason
      }, { withCredentials: true });

      if (response.data.authorized) {
        setAppliedDiscount({
          percent: parseFloat(discountPercent),
          reason: discountReason,
          authorized_by: response.data.authorized_by
        });
        toast.success(`Descuento del ${discountPercent}% autorizado por ${response.data.authorized_by}`);
        setShowDiscountDialog(false);
        setAdminPassword("");
        setDiscountPercent("");
        setDiscountReason("");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al verificar descuento");
    } finally {
      setIsVerifyingDiscount(false);
    }
  };

  // Process order without payment (for phone orders - pickup/delivery)
  const handleConfirmOrderWithoutPayment = async () => {

    // Validate based on order type
    // Auto-fill logic for speed (User Request)
    let finalTableNumber = tableNumber;
    let finalCustomerName = customerName;

    if (orderType === "local") {
      if (!finalTableNumber || !finalTableNumber.trim()) {
        // Find first available table
        const firstAvailable = availableTables.find(t => t.status === "available");

        if (firstAvailable) {
          finalTableNumber = firstAvailable.number;
          toast.success(`Mesa asignada automáticamente: ${finalTableNumber}`);
        } else {
          // Fallback if no tables available
          // Try to find ANY table
          const anyTable = availableTables[0];
          if (anyTable) {
            finalTableNumber = anyTable.number;
            toast.warning(`No hay mesas libres. Asignando mesa ${finalTableNumber}`);
          } else {
            finalTableNumber = "BARRA"; // Fallback to BARRA if absolutely no tables
            toast.info("Asignado a BARRA (Sin mesas configuradas)");
          }
        }
      }
    } else {
      // Pickup or Delivery auto-name
      if (!finalCustomerName || !finalCustomerName.trim()) {
        finalCustomerName = `Cliente ${Math.floor(Math.random() * 1000)}`;
        toast.info(`Nombre autogenerado: ${finalCustomerName}`);
      }
    }

    if (orderType === "delivery" && !deliveryAddress.trim()) {
      toast.error("La dirección es obligatoria para el reparto");
      return;
    }

    setIsProcessing(true);
    try {
      const orderData = {
        branch_id: selectedBranch !== "all" ? selectedBranch : null,
        items: cart.map(item => ({
          item_id: item.item_id,
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          is_menu: item.is_menu || false,
          extras: item.extras || [],
          notes: item.notes || ""
        })),
        order_type: orderType,
        table_number: orderType === "local" ? finalTableNumber : null,
        address: orderType === "delivery" ? {
          street: deliveryAddress,
          city: "Almería",
          postal_code: "04001"
        } : null,
        customer_name: finalCustomerName || (orderType === "local" ? `Mesa ${finalTableNumber}` : "Cliente Teléfono"),
        customer_phone: customerPhone || "",
        payment_method: "pending", // Will be set when customer pays
        // Add payment note to order notes if exists (for delivery change)
        notes: (orderNotes + (paymentNote && parseFloat(paymentNote) > total ? ` [PAGA CON: ${paymentNote}€ - CAMBIO: ${(parseFloat(paymentNote) - total).toFixed(2)}€]` : "")).trim(),
        subtotal: subtotal,
        delivery_fee: deliveryFee,
        total: total,
        payment_status: "pending", // Customer will pay later
        source: "pos"
      };

      if (editingOrderId) {
        // ACTUALIZAR PEDIDO EXISTENTE (Sin pago)
        await axios.put(`${API}/orders/${editingOrderId}`, {
          items: orderData.items,
          order_type: orderData.order_type,
          table_number: orderData.table_number,
          customer_name: orderData.customer_name,
          customer_phone: orderData.customer_phone,
          notes: orderData.notes,
          total: orderData.total
        }, { withCredentials: true });

        toast.success("Pedido actualizado y enviado a cocina", {
          icon: "🍳",
          duration: 5000
        });
        setEditingOrderId(null);
        setOriginalOrder(null);
        clearCart();
        setTableNumber("");
        setCustomerName("");
        fetchData();
      } else if (isOnline) {
        // Modo Online tradicional
        try {
          const response = await axios.post(`${API}/orders/pos`, orderData, {
            withCredentials: true
          });

          const fullOrder = response.data;

          toast.success(`Pedido #${fullOrder.order_id.slice(-6)} enviado a cocina - Pendiente de cobro`, {
            duration: 5000,
            icon: "🍳"
          });

          // IMMEDIATE PRINTING FOR KITCHEN (Fast-path)
          if (isTPV && printMethod === 'bridge') {
            try {
              console.log("[AdminPOS] Fast-path kitchen print...");
              const resK = await printProvider.printKitchenTicket(fullOrder);
              if (resK.success) {
                await axios.put(`${API}/orders/${fullOrder.order_id}/print-status`, { status: "printed", type: "kitchen" }, { withCredentials: true });
              } else {
                toast.error("Error Comanda: " + (resK.error || "Fallo desconocido"));
              }
            } catch (pError) {
              console.error("Error in immediate kitchen print:", pError);
              toast.error("Fallo sistema impresión: " + pError.message);
            }
          }

          // CRM: Save/Update Customer automatically for deferred orders too
          if (customerPhone && customerPhone.length >= 9) {
            try {
              await axios.post(`${API}/admin/customers`, {
                phone: customerPhone,
                name: finalCustomerName,
                address: deliveryAddress
              }, { withCredentials: true });
            } catch (crmError) {
              console.error("Error saving customer to CRM:", crmError);
            }
          }

          clearCart();
          fetchData(); // Refresh pending orders
        } catch (apiError) {
          // Error online, guardar offline
          console.error("API Error, guardando offline:", apiError);
          await offlineDB.savePendingOrder(orderData);
          toast.warning("⚠️ Sin conexión - Pedido guardado offline");
          clearCart();
        }
      } else {
        // Modo Offline
        await offlineDB.savePendingOrder(orderData);
        toast.warning("📴 MODO OFFLINE - Pedido guardado (se sincronizará automáticamente)");
        clearCart();
      }
    } catch (error) {
      console.error("Error creating order:", error);
      toast.error("Error al crear el pedido");
    } finally {
      setIsProcessing(false);
    }
  };

  // Print tickets function - sends to thermal printer
  const printTickets = async (orderId) => {
    if (isTablet) {
      toast.info("Impresión solo disponible en el TPV de Caja");
      return;
    }
    try {
      // Intentar usar el servicio centralizado que ya maneja localhost:8001 automáticamente
      const orderRes = await axios.get(`${API}/orders/${orderId}`, { withCredentials: true });
      const order = orderRes.data;

      console.log("[AdminPOS] Intentando imprimir mediante PrintService...");
      await printService.printOrder(order, "ZTAC TACOS");

      // Enviamos a cocina si es un pedido nuevo (aunque el service ya suele hacerlo si es kitchen)
      // Pero aquí estamos en POS, imprimimos el ticket de cliente principalmente.
      toast.success("Ticket enviado a impresora");
    } catch (error) {
      console.error("Error printing tickets:", error);
      toast.error("Error al imprimir tickets. Asegúrate de que el servicio de impresión esté activo.");
    }
  };

  // Print to thermal printer (Direct Browser Print via Hidden Iframe)
  const printDirectlyFromBrowser = (ticketText, ticketType = "ticket") => {
    try {
      console.log(`[AdminPOS] Printing ${ticketType} directly from browser...`);
      const iframeId = 'silent-print-iframe';
      let printFrame = document.getElementById(iframeId);

      if (!printFrame) {
        printFrame = document.createElement('iframe');
        printFrame.id = iframeId;
        printFrame.style.position = 'fixed';
        printFrame.style.right = '100%';
        printFrame.style.bottom = '100%';
        printFrame.style.width = '0';
        printFrame.style.height = '0';
        printFrame.style.border = 'none';
        document.body.appendChild(printFrame);
      }

      const printDoc = printFrame.contentWindow.document;
      printDoc.open();
      printDoc.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>ZTAC - ${ticketType}</title>
          <style>
            @page { size: 80mm auto; margin: 0; }
            body {
              font-family: 'Courier New', Courier, monospace;
              font-size: 14px;
              line-height: 1.2;
              margin: 0;
              padding: 10px;
              white-space: pre-wrap;
              color: black;
              background: white;
            }
            .bold { font-weight: bold; }
            .center { text-align: center; }
            hr { border: 0; border-top: 1px dashed black; margin: 5px 0; }
          </style>
        </head>
        <body>${ticketText}</body>
        <script>
          window.onload = function() {
            window.print();
          };
        </script>
        </html>
      `);
      printDoc.close();

      toast.success(`${ticketType.toUpperCase()} enviado a cola de Windows`);
    } catch (error) {
      console.error(`Error in direct printing:`, error);
      toast.error("Error en impresión directa");
    }
  };

  // Process order
  const handleProcessOrder = async () => {
    if (cart.length === 0) {
      toast.error("El carrito está vacío");
      return;
    }

    // Check if shift is active
    if (!activeShift) {
      toast.error("Debes abrir un turno antes de vender. Ve a Reporte de Caja para abrir turno.");
      setShowPayment(false);
      return;
    }


    // Validate based on order type
    // Auto-fill logic for speed (User Request)
    let finalTableNumber = tableNumber;
    let finalCustomerName = customerName;

    if (orderType === "local") {
      if (!finalTableNumber || !finalTableNumber.trim()) {
        const randomTable = Math.floor(Math.random() * 99) + 1;
        finalTableNumber = randomTable.toString();
        toast.info(`Mesa autogenerada: ${finalTableNumber}`);
      }
    } else {
      // Pickup or Delivery auto-name
      if (!finalCustomerName || !finalCustomerName.trim()) {
        finalCustomerName = `Cliente ${Math.floor(Math.random() * 1000)}`;
        toast.info(`Nombre autogenerado: ${finalCustomerName}`);
      }
    }

    if (orderType === "delivery" && !deliveryAddress.trim()) {
      toast.error("La dirección es obligatoria para el reparto");
      return;
    }

    if (isTablet) {
      // TABLET FLOW: Send as pre-order to Caja
      setIsProcessing(true);
      try {
        const orderData = {
          branch_id: selectedBranch !== "all" ? selectedBranch : null,
          items: cart.map(item => ({
            item_id: item.item_id,
            name: item.name,
            quantity: item.quantity,
            price: item.price,
            is_menu: item.is_menu || false,
            extras: item.extras || [],
            notes: item.notes || ""
          })),
          order_type: orderType,
          table_number: orderType === "local" ? finalTableNumber : null,
          customer_name: finalCustomerName || (orderType === "local" ? `Mesa ${finalTableNumber}` : "Cliente Tablet"),
          customer_phone: customerPhone || "",
          payment_method: "pending",
          notes: orderNotes ? `${orderNotes} (Enviado desde Tablet 1)` : "(Enviado desde Tablet 1)",
          subtotal: subtotal,
          delivery_fee: deliveryFee,
          total: total,
          payment_status: "pending",
          source: "tablet_pos",
          device_id: "tablet_1"
        };

        const response = await axios.post(`${API}/orders/pos`, orderData, { withCredentials: true });
        toast.success(`Pedido #${response.data.order_id.slice(-6)} enviado a Caja`);
        clearCart();
        fetchData();
      } catch (error) {
        console.error("Error sending order to caja:", error);
        toast.error("Error al enviar pedido a caja");
      } finally {
        setIsProcessing(false);
      }
      return;
    }

    setIsProcessing(true);
    try {
      const orderData = {
        branch_id: selectedBranch !== "all" ? selectedBranch : null,
        items: cart.map(item => ({
          item_id: item.item_id,
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          is_menu: item.is_menu || false,
          extras: item.extras || [],
          notes: item.notes || "",
          printed_quantity: item.printed_quantity || 0
        })),
        order_type: orderType,
        table_number: orderType === "local" ? finalTableNumber : null,
        address: orderType === "delivery" ? {
          street: deliveryAddress,
          city: "Almería",
          postal_code: "04001"
        } : null,
        customer_name: finalCustomerName || "Cliente Caja",
        customer_phone: customerPhone || "",
        payment_method: paymentMethod,
        notes: orderNotes,
        subtotal: subtotal,
        delivery_fee: deliveryFee,
        discount: discountAmount,
        discount_description: appliedDiscount ? `${appliedDiscount.percent}% - ${appliedDiscount.reason || 'Descuento'} (Auth: ${appliedDiscount.authorized_by})` : null,
        total: total,
        payment_status: paymentMethod === "cash" ? "paid" : "pending",
        source: "pos"
      };

      if (editingOrderId) {
        // ACTUALIZAR PEDIDO EXISTENTE
        await axios.put(`${API}/orders/${editingOrderId}`, {
          items: orderData.items,
          order_type: orderData.order_type,
          table_number: orderData.table_number,
          customer_name: orderData.customer_name,
          customer_phone: orderData.customer_phone,
          notes: orderData.notes,
          total: orderData.total
        }, { withCredentials: true });

        // Si se seleccionó pagar (EFECTIVO o TARJETA) y no es "pending", procesar el pago
        if (paymentMethod === "cash" || paymentMethod === "card") {
          try {
            await axios.put(`${API}/orders/${editingOrderId}/pay`, {
              payment_method: paymentMethod
            }, { withCredentials: true });
            toast.success(`Pago registrado: ${paymentMethod === 'cash' ? 'Efectivo' : 'Tarjeta'}`, { icon: "💰" });

            // Imprimir tras cobro - Desactivado (El PrintQueueWorker lo hará)
            /*
            if (originalOrder) {
              printService.printOrder({ ...originalOrder, payment_status: 'paid', payment_method: paymentMethod }, "ZTAC TACOS");
            }
            */
          } catch (payError) {
            console.error("Error processing payment update:", payError);
            toast.error("Pedido actualizado pero falló el cobro. Intente cobrar nuevamente.");
          }
        }

        toast.success("Pedido actualizado con éxito", { icon: "✅" });
        setEditingOrderId(null);
        setOriginalOrder(null);
        clearCart();
        setShowPayment(false);
        fetchData();
      } else if (isOnline) {
        // CREAR NUEVO PEDIDO ONLINE
        try {
          const response = await axios.post(`${API}/orders/pos`, orderData, {
            withCredentials: true
          });

          const orderId = response.data.order_id;
          const fullOrder = response.data;

          toast.success(`Pedido #${orderId.slice(-6)} creado`);

          // Immediate Printing logic for TPV (Bypass worker for speed)
          if (isTPV && printMethod === 'bridge') {
            try {
              console.log("[AdminPOS] Fast-path printing via ZT-Bridge...");
              const resK = await printProvider.printKitchenTicket(fullOrder);
              if (resK.success) {
                await axios.put(`${API}/orders/${orderId}/print-status`, { status: "printed", type: "kitchen" }, { withCredentials: true });
              } else {
                toast.error("Error Comanda: " + (resK.error || "Fallo desconocido"));
              }
              
              if (paymentMethod === "cash" || paymentMethod === "card") {
                const resC = await printProvider.printCustomerTicket(fullOrder);
                if (resC.success) {
                  await axios.put(`${API}/orders/${orderId}/print-status`, { status: "printed", type: "customer" }, { withCredentials: true });
                } else {
                  toast.error("Error Ticket: " + (resC.error || "Fallo desconocido"));
                }
              }
            } catch (pError) {
              console.error("Error in immediate bridge print:", pError);
              toast.error("Fallo sistema impresión: " + pError.message);
            }
          } else if (isTPV && printMethod === 'browser') {
            // Fallback to direct browser print if configured
            try {
              const kitchenRes = await axios.get(`${API}/orders/${orderId}/print/kitchen`, { withCredentials: true });
              if (kitchenRes.data.ticket) {
                printDirectlyFromBrowser(kitchenRes.data.ticket, "comanda");
              }
              if (paymentMethod === "cash" || paymentMethod === "card") {
                const customerRes = await axios.get(`${API}/orders/${orderId}/print/customer`, { withCredentials: true });
                if (customerRes.data.ticket) {
                  setTimeout(() => printDirectlyFromBrowser(customerRes.data.ticket, "ticket"), 1000);
                }
              }
            } catch (pError) {
              console.error("Error in browser print fallback:", pError);
            }
          }
          // Note: If autoPrintWorker is ON, it will pick it up automatically from the queue.

          // CRM: Save/Update Customer automatically
          if (customerPhone && customerPhone.length >= 9) {
            try {
              await axios.post(`${API}/admin/customers`, {
                phone: customerPhone,
                name: finalCustomerName,
                address: deliveryAddress
              }, { withCredentials: true });
            } catch (crmError) {
              console.error("Error saving customer to CRM:", crmError);
            }
          }

          clearCart();
          setShowPayment(false);
          setCashReceived("");
          fetchData(); // Refresh pending orders

        } catch (apiError) {
          // Si hay error online, guardar offline como fallback
          console.error("API Error, guardando offline:", apiError);
          await offlineDB.savePendingOrder(orderData, user?.company_id);
          toast.warning("⚠️ Sin conexión - Pedido guardado offline");

          clearCart();
          setShowPayment(false);
          setCashReceived("");
        }
      } else {
        // Modo Offline: Guardar en IndexedDB
        await offlineDB.savePendingOrder(orderData, user?.company_id);
        toast.warning("📴 MODO OFFLINE - Pedido guardado (se sincronizará automáticamente)");

        clearCart();
        setShowPayment(false);
        setCashReceived("");
      }
    } catch (error) {
      console.error("Error creating order:", error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || "Error al crear el pedido";
      toast.error(errorMsg);
    } finally {
      setIsProcessing(false);
    }
  };

  // Open payment dialog for pending order
  const openPendingPaymentDialog = (order) => {
    setSelectedPendingOrder(order);
    setPendingPaymentMethod("cash");
    setCashReceived("");
    setShowPendingPayment(true);
  };

  // Process payment for pending order
  const handlePayPendingOrder = async () => {
    if (!selectedPendingOrder) return;

    setIsProcessing(true);
    try {
      await axios.put(`${API}/orders/${selectedPendingOrder.order_id}/pay`, {
        payment_method: pendingPaymentMethod
      }, {
        withCredentials: true
      });

      const methodLabel = pendingPaymentMethod === "cash" ? "Efectivo" : "Tarjeta";
      toast.success(`Pedido #${selectedPendingOrder.order_id.slice(-6)} cobrado (${methodLabel}) - ${selectedPendingOrder.total.toFixed(2)} €`);

      // IMMEDIATE PRINTING FOR CUSTOMER (Fast-path)
      if (isTPV && printMethod === 'bridge') {
        try {
          console.log("[AdminPOS] Fast-path customer print (Pay)...");
          const resC = await printProvider.printCustomerTicket(selectedPendingOrder);
          if (resC.success) {
            await axios.put(`${API}/orders/${selectedPendingOrder.order_id}/print-status`, { status: "printed", type: "customer" }, { withCredentials: true });
          } else {
            toast.error("Error Ticket: " + (resC.error || "Fallo desconocido"));
          }
        } catch (pError) {
          console.error("Error in immediate customer print:", pError);
          toast.error("Fallo sistema impresión: " + pError.message);
        }
      } else if (isTPV && printMethod === 'browser') {
        try {
          const customerRes = await axios.get(`${API}/orders/${selectedPendingOrder.order_id}/print/customer`, { withCredentials: true });
          if (customerRes.data.ticket) {
            printDirectlyFromBrowser(customerRes.data.ticket, "ticket");
          }
        } catch (pError) {
          console.error("Error fetching ticket for direct print:", pError);
        }
      }

      setSelectedPendingOrder(null);
      setShowPendingPayment(false);
      setCashReceived("");
      fetchData(); // Refresh pending orders
    } catch (error) {
      console.error("Error processing payment:", error);
      toast.error("Error al procesar el pago");
    } finally {
      setIsProcessing(false);
    }
  };

  // Calculate change
  const change = cashReceived ? parseFloat(cashReceived) - total : 0;
  const pendingChange = selectedPendingOrder && cashReceived
    ? parseFloat(cashReceived) - (parseFloat(selectedPendingOrder?.total) || 0)
    : 0;

  if (isLoading) {
    return (
      <AdminLayout title="TPV / Caja">
        <div className="h-[calc(100vh-120px)] flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-4 border-yellow-500 border-t-transparent rounded-full" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="TPV / Caja" noPadding noFooter>
      <div className="flex flex-col h-[calc(100vh-0px)] lg:h-screen bg-gray-900 text-white overflow-hidden p-0 m-0">
        {/* Render Invisible Worker (Only if it is TPV) */}
        {isTPV && selectedBranch && selectedBranch !== 'all' && (
          <PrintQueueWorker
            branchId={selectedBranch}
            onStatusChange={setPrintQueueStatus}
            isTPV={isTPV}
          />
        )}

        {/* Header */}
        <header className="flex-none bg-gray-800 border-b border-gray-700 p-2 sm:p-4 flex items-center justify-between shadow-md z-10">
          <div className="flex items-center gap-3">
            <OnlineStatusIndicator />
            {/* TPV Mode Selector (Discreet for production) */}
            <div className="flex bg-black/40 p-1 rounded-lg border border-gray-700">
                <Button
                  variant="ghost"
                  size="sm"
                  className={`h-9 px-3 text-xs font-bold ${isTPV ? 'bg-yellow-500 text-black hover:bg-yellow-400 shadow-lg' : 'text-gray-400'}`}
                  onClick={() => {
                    localStorage.setItem('zt_device_role', 'caja');
                    setDeviceRole('caja');
                    toast.success("Modo CAJA activado");
                    window.location.reload();
                  }}
                >
                  ESTA ES LA CAJA
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className={`h-9 px-3 text-xs font-bold ${isTablet ? 'bg-blue-600 text-white hover:bg-blue-500 shadow-lg' : 'text-gray-400'}`}
                  onClick={() => {
                    localStorage.setItem('zt_device_role', 'tablet');
                    setDeviceRole('tablet');
                    toast.success("Modo TABLET activado");
                    window.location.reload();
                  }}
                >
                  ESTA ES UNA TABLET
                </Button>
            </div>

            {/* Printer Status & Queue (Only on TPV) */}
            {isTPV && (
              <div className="flex items-center gap-2">
                <Button
                  variant={printQueueStatus.count > 0 ? "destructive" : "outline"}
                  size="sm"
                  className={`h-9 border-gray-600 gap-2 ${printQueueStatus.processing ? "animate-pulse" : ""}`}
                  title="Cola de impresión"
                  onClick={() => setShowPrintSettings(true)}
                >
                  <Printer className={`w-4 h-4 ${printQueueStatus.count > 0 ? "text-white" : "text-gray-400"}`} />
                  {printQueueStatus.count > 0 && (
                    <span className="font-bold">{printQueueStatus.count}</span>
                  )}
                </Button>

                <div
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium cursor-pointer transition-colors ${printerStatus === "connected"
                    ? "bg-green-500/10 border-green-500/50 text-green-500 hover:bg-green-500/20"
                    : printerStatus === "disconnected"
                      ? "bg-red-500/10 border-red-500/50 text-red-500 hover:bg-red-500/20"
                      : "bg-yellow-500/10 border-yellow-500/50 text-yellow-500"
                    }`}
                  onClick={() => setShowPrintSettings(true)}
                >
                  <Printer className={`w-3.5 h-3.5 ${printerStatus === 'connected' ? 'text-green-500' : 'text-current'}`} />
                  <span className="text-[10px] hidden sm:inline">
                    {printerStatus === 'connected' ? "TICKERA OK" :
                      printerStatus === 'disconnected' ? "SIN TICKERA" : "BUSCANDO..."}
                  </span>
                </div>
              </div>
            )}

            {editingOrderId && (
              <Badge className="bg-blue-600 animate-pulse ml-2">MODO EDICIÓN</Badge>
            )}
          </div>
        </header>

        {((user?.role === "admin" && (!user?.assigned_branches || user?.assigned_branches.length !== 1)) || (user?.assigned_branches?.length > 1)) && (
          <div className="w-[200px]">
            <Select value={selectedBranch || "all"} onValueChange={setSelectedBranch}>
              <SelectTrigger className="bg-gray-900 border-gray-800 text-white h-9">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <SelectValue placeholder="Sucursal" />
                </div>
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-gray-800 text-white">
                <SelectItem value="all">Todas las sucursales</SelectItem>
                {branches.map(b => (
                  <SelectItem key={b.branch_id} value={b.branch_id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* No Active Shift Warning */}
        {
          !checkingShift && !activeShift && (
            <div className="bg-red-900/30 border-2 border-red-500 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-8 h-8 text-red-500 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-red-400 font-bold text-lg">Sin turno activo</p>
                  <p className="text-red-300 text-sm">Debes abrir un turno para {selectedBranch !== 'all' ? branches.find(b => b.branch_id === selectedBranch)?.name : 'la sucursal'} en Reporte de Caja antes de poder vender</p>
                </div>
                <Button
                  onClick={() => window.location.href = `/admin/cash?branch_id=${selectedBranch}`}
                  className="bg-red-600 hover:bg-red-500 text-white"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Abrir Turno
                </Button>
              </div>
            </div>
          )
        }

        {/* Edit Mode Banner */}
        {
          editingOrderId && (
            <div className="bg-blue-600 border-2 border-blue-400 rounded-xl p-4 flex items-center justify-between shadow-lg animate-pulse-subtle">
              <div className="flex items-center gap-3">
                <div className="bg-white p-2 rounded-full">
                  <Edit2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-white font-black text-lg">MODO EDICIÓN ACTIVADO</p>
                  <p className="text-blue-100 text-sm font-medium">Actualizando Pedido #{editingOrderId.slice(-6)} - {originalOrder?.customer_name || `Mesa ${originalOrder?.table_number}`}</p>
                </div>
              </div>
              <Button
                onClick={cancelEditMode}
                variant="outline"
                className="bg-white text-blue-600 hover:bg-blue-50 border-none font-bold"
              >
                <X className="w-4 h-4 mr-2" />
                CANCELAR
              </Button>
            </div>
          )
        }

        <div className="flex gap-4 flex-1 overflow-hidden min-h-0">
          {/* Left Panel - Menu or Pending Orders */}
          <div className="flex-1 flex flex-col bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
            {/* View Mode Toggle */}
            <div className="flex p-3 border-b border-gray-800 gap-2">
              <Button
                size="sm"
                onClick={() => setViewMode("new")}
                className={viewMode === "new"
                  ? "bg-yellow-500 text-black hover:bg-yellow-400 flex-1"
                  : "bg-gray-800 text-gray-300 hover:bg-gray-700 flex-1"}
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Nuevo Pedido
              </Button>
              <Button
                size="sm"
                onClick={() => setViewMode("pending")}
                className={viewMode === "pending"
                  ? "bg-green-600 text-white hover:bg-green-500 flex-1"
                  : "bg-gray-800 text-gray-300 hover:bg-gray-700 flex-1"}
              >
                <Receipt className="w-4 h-4 mr-2" />
                {isTPV ? "Por Cobrar" : "Tablet / Pendientes"} ({pendingOrders.length})
              </Button>
              <Button
                size="sm"
                onClick={() => setViewMode("history")}
                className={viewMode === "history"
                  ? "bg-blue-600 text-white hover:bg-blue-500 flex-1"
                  : "bg-gray-800 text-gray-300 hover:bg-gray-700 flex-1"}
              >
                <History className="w-4 h-4 mr-2" />
                Historial
              </Button>
            </div>

            {viewMode === "new" ? (
              <>
                {/* Categories */}
                <div className="flex gap-2 p-3 border-b border-gray-800 overflow-x-auto">
                  {categories.map(cat => (
                    <Button
                      key={cat.category_id}
                      size="sm"
                      variant={selectedCategory === cat.category_id ? "default" : "outline"}
                      onClick={() => setSelectedCategory(cat.category_id)}
                      className={selectedCategory === cat.category_id
                        ? "bg-yellow-500 text-black hover:bg-yellow-400 whitespace-nowrap"
                        : "border-gray-700 text-gray-300 hover:text-white whitespace-nowrap"}
                    >
                      {cat.icon} {cat.name}
                    </Button>
                  ))}
                </div>

                {/* Products Grid */}
                <div className="flex-1 p-3 overflow-y-auto">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">

                    {/* ===== CREA TU TACO — tarjeta fija ===== */}
                    {(() => {
                      const tacoCat = selectedCategory && categories.find(c => c.category_id === selectedCategory);
                      const isTacoCategory = !selectedCategory ||
                        (tacoCat && (
                          tacoCat.category_id.toLowerCase().includes('taco') ||
                          tacoCat.name.toLowerCase().includes('taco')
                        ));
                      if (!isTacoCategory) return null;

                      // base_price IS the M absolute price; tortilla prices are increments over it
                      const basePrice = customTacoConfig
                        ? (parseFloat(customTacoConfig.base_price) || 0)
                        : 5.0;

                      return (
                        <button
                          key="__crea_tu_taco__"
                          onClick={() => {
                            setTacoConfig({ size: 'M', sauces: [], meats: [], extras: [], isGratinado: false, gratinadoQueso: 'Mozzarella', isMenu: false, baseItem: null });
                            setShowTacoBuilder(true);
                          }}
                          className="bg-gradient-to-br from-yellow-600 to-orange-600 hover:from-yellow-500 hover:to-orange-500 rounded-lg p-3 text-left transition-all hover:scale-105 border-2 border-yellow-500 hover:border-yellow-300 relative overflow-hidden shadow-lg"
                        >
                          {/* shimmer */}
                          <div className="absolute inset-0 bg-white/5 rounded-lg" />
                          <div className="aspect-square rounded-lg overflow-hidden mb-2 bg-yellow-900/40 flex items-center justify-center relative">
                            <UtensilsCrossed className="w-12 h-12 text-yellow-200 opacity-80" />
                            <span className="absolute top-1 right-1 bg-yellow-400 text-black text-[9px] font-black px-1.5 py-0.5 rounded-full">CUSTOM</span>
                          </div>
                          <h3 className="font-black text-white text-sm truncate">🌮 CREA TU ZTACO</h3>
                          <div className="flex justify-between items-center">
                            <p className="text-yellow-200 font-bold text-xs">desde {basePrice.toFixed(2)} €</p>
                            <p className="text-[9px] text-yellow-300/70 font-medium">personalizado</p>
                          </div>
                        </button>
                      );
                    })()}
                    {/* ======================================= */}

                    {filteredItems.map(item => (
                      <button
                        key={item.item_id}
                        onClick={() => addToCart(item)}
                        className="bg-gray-800 hover:bg-gray-700 rounded-lg p-3 text-left transition-all hover:scale-105 border border-gray-700 hover:border-yellow-500"
                      >
                        <div className="aspect-square rounded-lg overflow-hidden mb-2 bg-gray-700">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-full h-full object-cover"
                            onError={(e) => e.target.src = "https://images.unsplash.com/photo-1565299585323-38d6b0865b47?w=200"}
                          />
                        </div>
                        <h3 className="font-bold text-white text-sm truncate">{item.name}</h3>
                        <div className="flex justify-between items-center">
                          <p className="text-yellow-500 font-bold">{parseFloat(item.price).toFixed(2)} €</p>
                          {item.menu_price && (
                            <p className="text-[10px] text-gray-400 font-medium">MENU: {parseFloat(item.menu_price).toFixed(2)} €</p>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

              </>
            ) : viewMode === "pending" ? (
              /* Pending Orders View */
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* Filters */}
                <div className="flex gap-2 p-3 border-b border-gray-800">
                  <Button
                    size="sm"
                    onClick={() => setPendingFilter("all")}
                    className={pendingFilter === "all"
                      ? "bg-yellow-500 text-black hover:bg-yellow-400"
                      : "bg-gray-800 text-gray-300 hover:bg-gray-700"}
                  >
                    Todos ({baseForCounts.length})
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setPendingFilter("local")}
                    className={pendingFilter === "local"
                      ? "bg-green-500 text-black hover:bg-green-400"
                      : "bg-gray-800 text-gray-300 hover:bg-gray-700"}
                  >
                    🍽️ Local ({localCount})
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setPendingFilter("delivery")}
                    className={pendingFilter === "delivery"
                      ? "bg-blue-500 text-white hover:bg-blue-400"
                      : "bg-gray-800 text-gray-300 hover:bg-gray-700"}
                  >
                    🛵 Delivery ({deliveryCount})
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setPendingFilter("pickup")}
                    className={pendingFilter === "pickup"
                      ? "bg-yellow-500 text-black hover:bg-yellow-400"
                      : "bg-gray-800 text-gray-300 hover:bg-gray-700"}
                  >
                    🛍️ Pickup ({pickupCount})
                  </Button>
                </div>

                {/* Search Bar */}
                <div className="px-3 pb-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Buscar por #pedido, nombre, mesa, teléfono..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="bg-gray-800 border-gray-700 text-white pl-10"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery("")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Orders List */}
                <div className="flex-1 p-3 overflow-y-auto">
                  {filteredPendingOrders.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center p-6 bg-gray-900/50 rounded-xl border border-dashed border-gray-800">
                      {searchQuery ? (
                        <>
                          <Search className="w-12 h-12 mb-4 text-gray-600" />
                          <p className="text-white font-bold text-lg">No se encontró el pedido</p>
                          <p className="text-gray-500 text-sm mb-4">No hay pedidos pendientes que coincidan con "{searchQuery}"</p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSearchQuery("")}
                            className="border-gray-700 text-gray-400"
                          >
                            Ver todos los pedidos
                          </Button>
                        </>
                      ) : (
                        <>
                          <Check className="w-16 h-16 mb-4 text-green-500" />
                          <p className="text-xl font-bold text-white">¡Todo cobrado!</p>
                          <p className="text-sm text-gray-500">No hay pedidos pendientes de pago en este momento</p>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredPendingOrders.map(order => {
                        const isQR = order.source === 'qr';
                        const isTabletOrder = order.source === 'tablet' || order.source === 'tablet_pos';
                        
                        return (
                          <div
                            key={order.order_id}
                            className={`bg-gray-800 rounded-lg p-4 border-2 transition-all cursor-pointer hover:border-green-500 ${selectedPendingOrder?.order_id === order.order_id
                              ? "border-green-500"
                              : "border-gray-700"
                              }`}
                            onClick={() => setSelectedPendingOrder(order)}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                {isTabletOrder && <Badge className="bg-blue-600 border-2 border-white text-white font-black text-[10px] animate-pulse">TABLET</Badge>}
                                {isQR && <Badge className="bg-purple-600 text-[10px]">QR MENU</Badge>}
                                {!isQR && !isTabletOrder && <Badge className="bg-gray-600 text-[10px]">POS</Badge>}
                                <span className="font-bold text-white">#{order.order_id.slice(-6)}</span>
                                <Badge className={
                                  order.status === "sent_to_cashier" 
                                    ? "bg-yellow-500 text-black animate-pulse" 
                                    : order.order_type === "local" ? "bg-green-500 text-black" :
                                    order.order_type === "pickup" ? "bg-yellow-500 text-black" :
                                      "bg-blue-500 text-white"
                              }>
                                {order.status === "sent_to_cashier" ? "PRE-ORDEN QR" :
                                  order.order_type === "local" ? "🍽️ LOCAL" :
                                    order.order_type === "pickup" ? "🛍️ RECOGER" :
                                      "🛵 DELIVERY"}
                              </Badge>
                              <Badge className="bg-gray-600">
                                {order.status === "sent_to_cashier" ? "ESPERANDO COBRO" : order.status}
                              </Badge>
                              {order.is_addition && (
                                <Badge className="bg-orange-600 text-white animate-pulse">AGREGADO</Badge>
                              )}
                            </div>
                            <span className="font-bold text-yellow-500 text-lg">{order.total.toFixed(2)} €</span>
                          </div>

                          <div className="text-sm text-gray-400 mb-2 flex flex-wrap gap-2">
                            {order.order_type === "local" && order.table_number && (
                              <span className="text-green-400 font-medium">
                                {!isNaN(order.table_number) ? `Mesa ${order.table_number}` : `Loc: ${order.table_number}`}
                              </span>
                            )}
                            
                            {/* Identificación de Pre-orden QR */}
                            {(order.customer_name || order.customer_alias) ? (
                              <div className="flex items-center gap-1.5 bg-yellow-500/10 px-2 py-0.5 rounded border border-yellow-500/20">
                                <span className="text-[10px] text-yellow-500 font-bold uppercase truncate max-w-[80px]">👤 {order.customer_name || order.customer_alias}</span>
                                <span className="text-gray-600 text-[10px]">|</span>
                                <span className="text-[10px] text-yellow-500 font-black tracking-widest">{order.preorder_code || 'SIN REF'}</span>
                              </div>
                            ) : order.source === "qr" ? (
                              <div className="flex items-center gap-1.5 bg-red-500/10 px-2 py-0.5 rounded border border-red-500/20">
                                <span className="text-[10px] text-red-500 font-bold">⚠️ SIN IDENTIFICAR</span>
                                <span className="text-gray-600 text-[10px]">|</span>
                                <span className="text-[10px] text-red-500 font-black tracking-widest">{order.preorder_code || 'S/REF'}</span>
                              </div>
                            ) : null}

                            {/* Mostrar nombre del cliente si existe, si no mostrar quién lo creó */}
                            {order.customer_name && order.customer_name !== "Cliente Caja" ? (
                              <span className="text-white font-bold">• {order.customer_name}</span>
                            ) : (
                              !order.customer_alias && order.user_name && <span className="text-gray-500">• {order.user_name}</span>
                            )}

                            <span className="text-gray-600">•</span>
                            <span>{new Date(order.created_at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}</span>
                          </div>

                          <div className="text-sm text-gray-300">
                            {order.items.slice(0, 3).map((item, i) => (
                              <span key={i}>
                                {item.quantity}x {item.name}
                                {i < Math.min(order.items.length, 3) - 1 && ", "}
                              </span>
                            ))}
                            {order.items.length > 3 && <span className="text-gray-500"> +{order.items.length - 3} más</span>}

                            <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t border-gray-700">
                              <button
                                type="button"
                                className="flex-1 h-8 px-2 text-xs border border-orange-500/50 text-orange-400 hover:bg-orange-500 hover:text-black rounded transition-colors flex items-center justify-center gap-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (isTPV && printMethod === 'bridge') {
                                    printProvider.printKitchenTicket(order).then(res => {
                                      if (res.success) toast.success("Comanda (Bridge) enviada");
                                      else {
                                        axios.post(`${API}/orders/${order.order_id}/reprint/kitchen`, {}, { withCredentials: true })
                                          .then(() => toast.success("Encolado para impresión"));
                                      }
                                    });
                                  } else {
                                    axios.post(`${API}/orders/${order.order_id}/reprint/kitchen`, {}, { withCredentials: true })
                                      .then(() => toast.success("Comanda enviada"));
                                  }
                                }}
                              >
                                <Printer className="w-3 h-3" /> COMANDA
                              </button>
                              <button
                                type="button"
                                className="flex-1 h-8 px-2 text-xs border border-gray-600 text-gray-400 hover:bg-gray-700 hover:text-white rounded transition-colors flex items-center justify-center gap-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (isTPV && printMethod === 'bridge') {
                                    printProvider.printCustomerTicket(order).then(res => {
                                      if (res.success) toast.success("Ticket (Bridge) enviado");
                                      else {
                                        axios.post(`${API}/orders/${order.order_id}/reprint/customer`, {}, { withCredentials: true })
                                          .then(() => toast.success("Encolado para impresión"));
                                      }
                                    });
                                  } else {
                                    axios.post(`${API}/orders/${order.order_id}/reprint/customer`, {}, { withCredentials: true })
                                      .then(() => toast.success("Ticket enviado"));
                                  }
                                }}
                              >
                                <Printer className="w-3 h-3" /> TICKET
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* History View */
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* Filters Top Bar */}
                <div className="p-3 border-b border-gray-800 space-y-3 bg-gray-900/50">
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    <Select
                      value={historyFilters.preset}
                      onValueChange={(v) => setHistoryFilters(prev => ({ ...prev, preset: v, offset: 0 }))}
                    >
                      <SelectTrigger className="w-[140px] bg-gray-800 border-gray-700 text-white h-8 text-xs">
                        <Calendar className="w-3 h-3 mr-2" />
                        <SelectValue placeholder="Periodo" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700 text-white">
                        <SelectItem value="today">Hoy</SelectItem>
                        <SelectItem value="yesterday">Ayer</SelectItem>
                        <SelectItem value="last7">Últimos 7 días</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select
                      value={historyFilters.status}
                      onValueChange={(v) => setHistoryFilters(prev => ({ ...prev, status: v, offset: 0 }))}
                    >
                      <SelectTrigger className="w-[120px] bg-gray-800 border-gray-700 text-white h-8 text-xs">
                        <SelectValue placeholder="Estado" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700 text-white">
                        <SelectItem value="all">Cualquier Estado</SelectItem>
                        <SelectItem value="paid">Pagados (Cobrados)</SelectItem>
                        <SelectItem value="cancelled">Cancelados</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select
                      value={historyFilters.type}
                      onValueChange={(v) => setHistoryFilters(prev => ({ ...prev, type: v, offset: 0 }))}
                    >
                      <SelectTrigger className="w-[120px] bg-gray-800 border-gray-700 text-white h-8 text-xs">
                        <SelectValue placeholder="Tipo" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700 text-white">
                        <SelectItem value="all">Cualquier Tipo</SelectItem>
                        <SelectItem value="local">Local</SelectItem>
                        <SelectItem value="delivery">Delivery</SelectItem>
                        <SelectItem value="pickup">Pickup</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Buscar #pedido, cliente, mesa..."
                      value={historyFilters.q}
                      onChange={(e) => setHistoryFilters(prev => ({ ...prev, q: e.target.value, offset: 0 }))}
                      className="bg-gray-800 border-gray-700 text-white pl-10 h-8 text-xs font-mono"
                    />
                  </div>
                </div>

                {/* Table / List */}
                <div className="flex-1 overflow-y-auto p-3">
                  {isLoadingHistory ? (
                    <div className="flex justify-center items-center h-full py-20">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
                    </div>
                  ) : historyOrders.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center text-gray-500 py-10">
                      <History className="w-12 h-12 mb-4 opacity-20" />
                      <p className="font-bold">No se encontraron pedidos</p>
                      <p className="text-sm">Ajusta los filtros para ver más resultados</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {historyOrders.map(order => (
                        <div
                          key={order.order_id}
                          onClick={() => handleViewHistoryDetail(order)}
                          className="bg-gray-800/50 hover:bg-gray-800 border border-gray-800 hover:border-blue-500/50 rounded-lg p-3 transition-all cursor-pointer group"
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex items-center gap-2">
                              <span className="text-white font-bold text-sm">#{order.order_id.slice(-6).toUpperCase()}</span>
                              <Badge className={`text-[10px] px-1 py-0 h-4 ${order.payment_status === 'paid' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                                order.status === 'cancelled' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                                  'bg-yellow-500/20 text-yellow-500 border-yellow-500/30'
                                }`} variant="outline">
                                {order.payment_status === 'paid' ? 'PAGADO' : order.status.toUpperCase()}
                              </Badge>
                              <span className="text-gray-500 text-[10px]">
                                {new Date(order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            <span className="text-white font-bold text-sm tracking-tight">{(order.total || 0).toFixed(2)}€</span>
                          </div>
                          <div className="mt-1 flex justify-between items-end">
                            <div className="text-[10px] text-gray-400">
                              {order.order_type === 'local' ? `🪑 Mesa ${order.table_number}` :
                                order.order_type === 'delivery' ? `🛵 Delivery: ${order.customer_name}` :
                                  `🛍️ Pickup: ${order.customer_name}`}
                            </div>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Eye className="w-3 h-3 text-blue-400" />
                              <span className="text-[10px] text-blue-400 uppercase font-black">Detalles</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Pagination */}
                <div className="p-2 border-t border-gray-800 flex items-center justify-between bg-gray-900/30">
                  <div className="text-[10px] text-gray-500">
                    Total: {historyTotal}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="w-7 h-7 hover:bg-gray-800"
                      disabled={historyFilters.offset === 0}
                      onClick={(e) => {
                        e.stopPropagation();
                        setHistoryFilters(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }));
                      }}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <div className="flex items-center px-2 text-[10px] text-white font-mono">
                      {Math.floor(historyFilters.offset / historyFilters.limit) + 1} / {Math.ceil(historyTotal / historyFilters.limit) || 1}
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="w-7 h-7 hover:bg-gray-800"
                      disabled={historyFilters.offset + historyFilters.limit >= historyTotal}
                      onClick={(e) => {
                        e.stopPropagation();
                        setHistoryFilters(prev => ({ ...prev, offset: prev.offset + prev.limit }));
                      }}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Panel - Cart or Payment */}
          <div className="w-96 flex flex-col bg-gray-900 rounded-xl border border-gray-800 overflow-hidden h-full">
            {viewMode === "pending" && selectedPendingOrder ? (
              /* Payment Panel for Pending Order */
              <div className="flex flex-col h-full">
                <div className="p-3 border-b border-gray-800 bg-green-900/30">
                  <h3 className="font-bold text-white text-center">
                    {selectedPendingOrder.status === "sent_to_cashier" ? "COBRAR PRE-ORDEN QR" : "COBRAR PEDIDO"}
                  </h3>
                  <p className="text-green-400 text-center text-sm">#{selectedPendingOrder.order_id.slice(-6)}</p>
                </div>
                <div className="flex-1 p-4 overflow-y-auto">
                  <div className="space-y-2 mb-4">
                    {selectedPendingOrder.items.map((item, i) => {
                      const itemPrice = parseFloat(item.price) || 0;
                      const itemQty = parseInt(item.quantity) || 1;
                      return (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-gray-300">{itemQty}x {item.name}</span>
                          <span className="text-white">{(itemPrice * itemQty).toFixed(2)} €</span>
                        </div>
                      );
                    })}
                  </div>
                  {selectedPendingOrder.order_type === "local" && selectedPendingOrder.table_number && (
                    <div className="bg-green-900/30 rounded-lg p-2 mb-4 text-center">
                      <span className="text-green-400 font-bold">Mesa {selectedPendingOrder.table_number}</span>
                    </div>
                  )}
                  <div className="border-t border-gray-700 pt-4">
                    <div className="flex justify-between text-xl font-bold mb-4">
                      <span className="text-white">TOTAL</span>
                      <span className="text-yellow-500">{(parseFloat(selectedPendingOrder.total) || 0).toFixed(2)} €</span>
                    </div>
                    {/* Payment Method Selection */}
                    <div className="space-y-3 mb-4">
                      <label className="text-sm text-gray-400">Método de pago</label>
                      <div className="flex gap-2">
                        <Button
                          data-testid="payment-method-cash"
                          onClick={() => setPendingPaymentMethod("cash")}
                          className={`flex-1 h-10 ${pendingPaymentMethod === "cash"
                            ? "bg-green-500 hover:bg-green-600 text-black"
                            : "bg-gray-800 hover:bg-gray-700 text-gray-300"}`}
                        >
                          <Banknote className="w-4 h-4 mr-2" />
                          EFECTIVO
                        </Button>
                        <Button
                          data-testid="payment-method-card"
                          onClick={() => setPendingPaymentMethod("card")}
                          className={`flex-1 h-10 ${pendingPaymentMethod === "card"
                            ? "bg-blue-500 hover:bg-blue-600 text-white"
                            : "bg-gray-800 hover:bg-gray-700 text-gray-300"}`}
                        >
                          <CreditCard className="w-4 h-4 mr-2" />
                          TARJETA
                        </Button>
                      </div>
                    </div>
                    {/* Cash Calculator - only show for cash */}
                    {pendingPaymentMethod === "cash" && (
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm text-gray-400">Efectivo recibido</label>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            value={cashReceived}
                            onChange={(e) => setCashReceived(e.target.value)}
                            className="bg-gray-800 border-gray-700 text-white text-right text-lg"
                          />
                        </div>
                        {/* Quick amounts */}
                        <div className="grid grid-cols-4 gap-2">
                          {[5, 10, 20, 50].map(amount => (
                            <Button
                              key={amount}
                              variant="outline"
                              size="sm"
                              className="border-gray-700 text-gray-300 hover:bg-gray-800"
                              onClick={() => setCashReceived(amount.toString())}
                            >
                              {amount}€
                            </Button>
                          ))}
                        </div>
                        {cashReceived && pendingChange >= 0 && (
                          <div className="flex justify-between items-center bg-green-900/50 rounded-lg p-3">
                            <span className="text-green-400">Cambio</span>
                            <span className="text-green-400 font-bold text-xl">{pendingChange.toFixed(2)} €</span>
                          </div>
                        )}
                      </div>
                    )}
                    {/* Card payment info */}
                    {pendingPaymentMethod === "card" && (
                      <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-4 text-center">
                        <CreditCard className="w-12 h-12 text-blue-400 mx-auto mb-2" />
                        <p className="text-blue-400">Procesa el pago en el datáfono</p>
                        <p className="text-blue-400/70 text-sm">y confirma cuando esté completado</p>
                      </div>
                    )}
                  </div>
                </div>
                {/* Actions: Print Pre-check + Pay */}
                <div className="p-4 border-t border-gray-800 space-y-2">
                  <button
                    type="button"
                    className="w-full h-10 border border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white rounded-md flex items-center justify-center font-medium transition-colors"
                    onClick={() => {
                      if (selectedPendingOrder) {
                        toast.info("Imprimiendo pre-cuenta...");
                        // Force print as "Customer Ticket" without changing status
                        printService.printOrder(selectedPendingOrder, "ZTAC TACOS");
                      }
                    }}
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    IMPRIMIR PRE-CUENTA
                  </button>
                  <button
                    type="button"
                    className={`w-full text-lg h-14 rounded-md font-medium flex items-center justify-center ${pendingPaymentMethod === "cash"
                      ? "bg-green-600 hover:bg-green-500 text-white"
                      : "bg-blue-600 hover:bg-blue-500 text-white"
                      } ${isProcessing || (pendingPaymentMethod === "cash" && cashReceived && parseFloat(cashReceived) < (parseFloat(selectedPendingOrder.total) || 0)) ? "opacity-50 cursor-not-allowed" : ""}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!isProcessing) {
                        handlePayPendingOrder();
                      }
                    }}
                    disabled={isProcessing || (pendingPaymentMethod === "cash" && cashReceived && parseFloat(cashReceived) < (parseFloat(selectedPendingOrder.total) || 0))}
                  >
                    {isProcessing ? (
                      <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                    ) : (
                      <>
                        {pendingPaymentMethod === "cash" ? <Banknote className="w-5 h-5 mr-2" /> : <CreditCard className="w-5 h-5 mr-2" />}
                        COBRAR {(parseFloat(selectedPendingOrder.total) || 0).toFixed(2)} €
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="w-full h-10 rounded-md border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-800"
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedPendingOrder(null);
                      setCashReceived("");
                    }}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            ) : (
              /* Normal Cart Panel */
              <>
                {/* Order Type Toggle */}
                <div className="p-3 border-b border-gray-800">
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      className={`h-9 px-3 py-2 rounded-md font-medium inline-flex items-center justify-center text-xs ${orderType === "local"
                        ? "bg-green-500 hover:bg-green-600 text-black"
                        : "bg-gray-800 hover:bg-gray-700 text-gray-300"}`}
                      onClick={() => setOrderType("local")}
                    >
                      <UtensilsCrossed className="w-4 h-4 mr-1" />
                      LOCAL
                    </button>
                    <button
                      type="button"
                      className={`h-9 px-3 py-2 rounded-md font-medium inline-flex items-center justify-center text-xs ${orderType === "pickup"
                        ? "bg-yellow-500 hover:bg-yellow-600 text-black"
                        : "bg-gray-800 hover:bg-gray-700 text-gray-300"}`}
                      onClick={() => {
                        setOrderType("pickup");
                        setTableNumber("");
                      }}
                    >
                      <ShoppingBag className="w-4 h-4 mr-1" />
                      RECOGER
                    </button>
                    <button
                      type="button"
                      className={`h-9 px-3 py-2 rounded-md font-medium inline-flex items-center justify-center text-xs ${orderType === "delivery"
                        ? "bg-blue-500 hover:bg-blue-600 text-white"
                        : "bg-gray-800 hover:bg-gray-700 text-gray-300"}`}
                      onClick={() => {
                        setOrderType("delivery");
                        setTableNumber("");
                      }}
                    >
                      <Truck className="w-4 h-4 mr-1" />
                      DELIVERY
                    </button>
                  </div>
                  {/* Order Details */}
                  <div className="mt-3 space-y-2">
                    {orderType === "local" ? (
                      <div className="flex gap-2">
                        <Input
                          placeholder="Nº Mesa"
                          value={tableNumber}
                          onChange={(e) => setTableNumber(e.target.value)}
                          className="bg-gray-800 border-gray-700 text-white text-center text-lg font-bold w-24"
                          type="number"
                          inputMode="numeric"
                        />
                        <Input
                          placeholder="Nombre Cliente (Opcional)"
                          value={customerName}
                          onChange={(e) => setCustomerName(e.target.value)}
                          className="bg-gray-800 border-gray-700 text-white flex-1"
                        />
                      </div>
                    ) : orderType === "pickup" ? (
                      <div className="space-y-2 p-2 bg-yellow-500/5 rounded-lg border border-yellow-500/10">
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <Input
                              placeholder="Teléfono"
                              value={customerPhone}
                              onChange={(e) => setCustomerPhone(e.target.value)}
                              className={`bg-gray-800 border-gray-700 text-white ${isFrequentCustomer ? "border-green-500" : ""}`}
                            />
                            {isSearchingCustomer ? (
                              <div className="absolute right-2 top-1/2 -translate-y-1/2">
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-yellow-500" />
                              </div>
                            ) : (
                              <button
                                onClick={handleManualCustomerSearch}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-yellow-500"
                              >
                                <Search className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                          {customerPhone && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-10 w-8 text-gray-500 hover:text-red-400"
                              onClick={() => {
                                setCustomerPhone("");
                                setCustomerName("");
                                setIsFrequentCustomer(false);
                              }}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        <Input
                          placeholder="Nombre del cliente"
                          value={customerName}
                          onChange={(e) => setCustomerName(e.target.value)}
                          className={`bg-gray-800 border-gray-700 text-white text-center font-bold ${isFrequentCustomer ? "border-green-500" : ""}`}
                        />
                      </div>
                    ) : (
                      <div className="space-y-2 p-2 bg-blue-500/5 rounded-lg border border-blue-500/10">
                        {customerDataConfirmed && customerName ? (
                          /* COMPACT SUMMARY VIEW */
                          <div className="flex items-center justify-between p-2 bg-blue-900/20 rounded border border-blue-500/20">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-white text-sm">{customerName}</span>
                                {isFrequentCustomer && <span className="text-[10px] bg-green-500 text-black px-1 rounded font-bold">VIP</span>}
                              </div>
                              <div className="text-xs text-gray-400">{deliveryAddress}</div>
                              {customerPhone && <div className="text-xs text-gray-500">{customerPhone}</div>}
                            </div>
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setCustomerDataConfirmed(false)}>
                              <Edit className="w-4 h-4 text-blue-400" />
                            </Button>
                          </div>
                        ) : (
                          /* FULL INPUT FORM */
                          <>
                            <div className="flex gap-2">
                              <div className="relative flex-1">
                                <Input
                                  placeholder="Teléfono (Buscar)"
                                  value={customerPhone}
                                  onChange={(e) => setCustomerPhone(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      handleManualCustomerSearch();
                                      if (customerName) setCustomerDataConfirmed(true);
                                    }
                                  }}
                                  className={`bg-gray-800 border-gray-700 text-white ${isFrequentCustomer ? "border-green-500 border-2" : ""}`}
                                />
                                {isSearchingCustomer ? (
                                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-yellow-500" />
                                  </div>
                                ) : (
                                  <button
                                    onClick={handleManualCustomerSearch}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-blue-400"
                                  >
                                    <Search className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                              {(customerPhone || customerName || deliveryAddress) && (
                                <div className="flex">
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-10 w-8 text-gray-500 hover:text-red-400"
                                    onClick={() => {
                                      setCustomerPhone("");
                                      setCustomerName("");
                                      setDeliveryAddress("");
                                      setIsFrequentCustomer(false);
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                  {/* Manual collapse button if data is present */}
                                  {(isFrequentCustomer || (customerName && deliveryAddress)) && (
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-10 w-8 text-blue-500 hover:text-blue-400 bg-blue-500/10 ml-1"
                                      onClick={() => setCustomerDataConfirmed(true)}
                                      title="Ocultar detalles"
                                    >
                                      <ChevronUp className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>
                            <Input
                              placeholder="Nombre"
                              value={customerName}
                              onChange={(e) => setCustomerName(e.target.value)}
                              className={`flex-1 bg-gray-800 border-gray-700 text-white ${isFrequentCustomer ? "border-green-500/50" : ""}`}
                            />
                            <Input
                              placeholder="Dirección de entrega"
                              value={deliveryAddress}
                              onChange={(e) => setDeliveryAddress(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && customerName && deliveryAddress) {
                                  setCustomerDataConfirmed(true);
                                }
                              }}
                              className={`bg-gray-800 border-gray-700 text-white ${isFrequentCustomer ? "border-green-500/50" : ""}`}
                            />
                            {/* Manual Confirm Button for new users */}
                            {customerName && deliveryAddress && !isFrequentCustomer && (
                              <Button size="sm" className="w-full bg-blue-600 hover:bg-blue-500 text-xs h-7" onClick={() => setCustomerDataConfirmed(true)}>
                                Confirmar Datos
                              </Button>
                            )}
                          </>
                        )}

                        {/* Payment Note Always Visible but Compact */}
                        <div className="flex gap-2 mt-1 pt-1 border-t border-gray-700/50">
                          <div className="relative flex-1">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs font-bold">PAGA CON:</span>
                            <Input
                              placeholder="50"
                              value={paymentNote}
                              type="number"
                              onChange={(e) => setPaymentNote(e.target.value)}
                              className="bg-gray-800 border-gray-700 text-white pl-20"
                            />
                          </div>
                          <div className="flex-1 bg-gray-800 border border-gray-700 rounded-md flex items-center justify-center">
                            {paymentNote && !isNaN(paymentNote) && parseFloat(paymentNote) >= total ? (
                              <span className="text-yellow-500 font-bold">
                                CAMBIO: {(parseFloat(paymentNote) - total).toFixed(2)} €
                              </span>
                            ) : (
                              <span className="text-gray-500 text-xs">Sin cambio</span>
                            )}
                          </div>
                        </div>

                        {isFrequentCustomer && (
                          <div className="text-[10px] text-green-500 font-bold uppercase tracking-widest text-center animate-pulse mt-2">
                            ✨ Cliente Frecuente Detectado
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {/* Cart Items - Scrollable Area - Asegurando mínimo de altura para ver 2-3 items */}
                <div className="flex-1 overflow-y-auto p-2 min-h-[100px] scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent scrollbar-w-[4px]">
                  {cart.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-gray-500">
                      <ShoppingCart className="w-12 h-12 mb-2" />
                      <p>Carrito vacío</p>
                      <p className="text-sm">Selecciona productos del menú</p>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {cart.map(item => {
                        const itemPrice = parseFloat(item.price) || 0;
                        const itemQty = parseInt(item.quantity) || 0;
                        const isOld = editingOrderId && (item.printed_quantity > 0);
                        const isNewAdded = editingOrderId && (itemQty > (item.printed_quantity || 0));

                        return (
                          <div key={`${item.item_id}_${item.is_menu}`} className={`bg-gray-800 rounded-lg p-2 flex items-center gap-2 transition-colors ${isNewAdded ? 'border-l-4 border-blue-500 bg-blue-900/10' : ''}`}>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-0.5">
                                <h4 className="font-medium text-white text-xs">{item.name}</h4>
                                {isOld && !isNewAdded && <span className="text-[9px] px-1.5 py-0.5 bg-gray-700 text-gray-300 rounded uppercase font-bold">Ya en cocina</span>}
                                {isNewAdded && <span className="text-[9px] px-1.5 py-0.5 bg-blue-600 text-white rounded uppercase font-bold animate-pulse">Nuevo / Extra</span>}
                              </div>
                              <p className="text-yellow-500 text-xs text-nowrap">{(itemPrice * itemQty).toFixed(2)} €</p>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 text-gray-400 hover:text-white"
                                onClick={() => updateQuantity(item.item_id, -1, item.is_menu)}
                              >
                                <Minus className="w-3 h-3" />
                              </Button>
                              <span className="text-white font-bold w-5 text-center text-sm">{itemQty}</span>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 text-gray-400 hover:text-white"
                                onClick={() => updateQuantity(item.item_id, 1, item.is_menu)}
                              >
                                <Plus className="w-3 h-3" />
                              </Button>

                              {!(isOld) && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0 text-red-500 hover:text-red-400 hover:bg-red-500/10 ml-0.5"
                                  onClick={() => removeFromCart(item.item_id, item.is_menu)}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                {/* Notes */}
                <div className="px-2 pb-1">
                  <Input
                    placeholder="Notas del pedido..."
                    value={orderNotes}
                    onChange={(e) => setOrderNotes(e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white text-xs h-8"
                  />
                </div>
                {/* Totals */}
                <div className="px-3 py-2 border-t border-gray-800 space-y-1">
                  <div className="flex justify-between text-gray-400">
                    <span>Subtotal</span>
                    <span>{subtotal.toFixed(2)} €</span>
                  </div>
                  {orderType === "delivery" && (
                    <div className="flex justify-between text-gray-400">
                      <span>Envío</span>
                      <span>{deliveryFee.toFixed(2)} €</span>
                    </div>
                  )}
                  {/* Discount Section */}
                  {isTPV && (
                    appliedDiscount ? (
                      <div className="flex justify-between text-green-400 items-center">
                        <div className="flex items-center gap-1">
                          <Percent className="w-3 h-3" />
                          <span>Descuento ({appliedDiscount.percent}%)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span>-{discountAmount.toFixed(2)} €</span>
                          <button
                            type="button"
                            onClick={() => setAppliedDiscount(null)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setShowDiscountDialog(true)}
                        disabled={cart.length === 0}
                        className="w-full flex items-center justify-center gap-2 text-sm text-purple-400 hover:text-purple-300 py-1 disabled:opacity-50"
                      >
                        <Percent className="w-4 h-4" />
                        Aplicar descuento
                      </button>
                    )
                  )}

                  <div className="flex justify-between text-xl font-bold text-white pt-2 border-t border-gray-700">
                    <span>TOTAL</span>
                    <span className="text-yellow-500">{total.toFixed(2)} €</span>
                  </div>
                </div>
                <div className="px-3 py-2 border-t border-gray-800 space-y-2">
                  {/* Row 1: Payment Method (ONLY ON TPV) */}
                  {isTPV && (
                    <div className="flex gap-2 mb-2">
                      <button
                        type="button"
                        className="flex-1 h-9 px-2 bg-green-500 hover:bg-green-600 text-black font-bold rounded-md inline-flex items-center justify-center text-sm disabled:opacity-50 disabled:pointer-events-none"
                        onClick={() => {
                          setPaymentMethod("cash");
                          setShowPayment(true);
                        }}
                        disabled={(cart.length === 0 && !editingOrderId) || isProcessing}
                      >
                        <Banknote className="w-4 h-4 mr-1.5" />
                        EFECTIVO
                      </button>
                      <button
                        type="button"
                        className="flex-1 h-9 px-2 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-md inline-flex items-center justify-center text-sm disabled:opacity-50 disabled:pointer-events-none"
                        onClick={() => {
                          setPaymentMethod("card");
                          setShowPayment(true);
                        }}
                        disabled={(cart.length === 0 && !editingOrderId) || isProcessing}
                      >
                        <CreditCard className="w-4 h-4 mr-1.5" />
                        TARJETA
                      </button>
                    </div>
                  )}

                  {/* Row 2: Cancel (Small) + Confirm (Main/Tablet) */}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="w-[30%] h-12 px-0 border border-red-500 text-red-500 hover:bg-red-500 hover:text-white font-bold rounded-md inline-flex items-center justify-center transition-colors disabled:opacity-50 disabled:pointer-events-none"
                      onClick={clearCart}
                      disabled={cart.length === 0 || editingOrderId}
                      title={editingOrderId ? "No se puede cancelar en modo edición" : "Cancelar Pedido"}
                    >
                      <X className="w-5 h-5" />
                    </button>

                    <button
                      type="button"
                      className={`flex-1 h-12 px-2 font-black rounded-md inline-flex items-center justify-center text-sm transition-all active:scale-95 disabled:opacity-50 disabled:pointer-events-none ${isTablet ? 'bg-yellow-500 hover:bg-yellow-400 text-black shadow-lg border-b-4 border-yellow-700' : editingOrderId ? "bg-blue-500 hover:bg-blue-600 text-white" : "bg-orange-500 hover:bg-orange-600 text-black"}`}
                      onClick={isTablet ? handleProcessOrder : handleConfirmOrderWithoutPayment}
                      disabled={cart.length === 0 || isProcessing}
                    >
                      {isProcessing ? <RefreshCw className="w-5 h-5 animate-spin" /> : 
                       isTablet ? (
                         <>
                           <ChefHat className="w-4 h-4 mr-2" />
                           ENVIAR A CAJA
                         </>
                       ) : 
                       editingOrderId ? "GUARDAR" :
                        orderType === "local" ? "COCINA" :
                          orderType === "pickup" ? "CONFIRMAR" : "CONFIRMAR"}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Payment Dialog */}
        <Dialog open={showPayment} onOpenChange={setShowPayment}>
          <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-yellow-500 flex items-center gap-2">
                {paymentMethod === "cash" ? (
                  <><Banknote className="w-5 h-5" /> Pago en Efectivo</>
                ) : (
                  <><CreditCard className="w-5 h-5" /> Pago con Tarjeta</>
                )}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Order Summary */}
              <div className="bg-gray-800 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-400">Tipo:</span>
                  <Badge className={
                    orderType === "local" ? "bg-green-500 text-black" :
                      orderType === "pickup" ? "bg-yellow-500 text-black" :
                        "bg-blue-500"
                  }>
                    {orderType === "local" ? `🍽️ Mesa ${tableNumber}` :
                      orderType === "pickup" ? `🛍️ ${customerName}` :
                        "🛵 Delivery"}
                  </Badge>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-400">Items:</span>
                  <span className="text-white">{cart.reduce((sum, i) => sum + i.quantity, 0)} productos</span>
                </div>
                <div className="flex justify-between items-center text-xl font-bold pt-2 border-t border-gray-700">
                  <span className="text-white">TOTAL:</span>
                  <span className="text-yellow-500">{total.toFixed(2)} €</span>
                </div>
              </div>

              {/* Cash Calculator */}
              {paymentMethod === "cash" && (
                <div className="space-y-3">
                  <label className="text-sm text-gray-400">Efectivo recibido:</label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={cashReceived}
                    onChange={(e) => setCashReceived(e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white text-2xl text-center font-bold h-14"
                    autoFocus
                  />

                  {/* Quick amounts */}
                  <div className="grid grid-cols-4 gap-2">
                    {[5, 10, 20, 50].map(amount => (
                      <Button
                        key={amount}
                        variant="outline"
                        className="border-gray-700 text-gray-300 hover:bg-gray-800"
                        onClick={() => setCashReceived(amount.toString())}
                      >
                        {amount}€
                      </Button>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    className="w-full border-gray-700 text-gray-300 hover:bg-gray-800"
                    onClick={() => setCashReceived(total.toFixed(2))}
                  >
                    Importe exacto ({total.toFixed(2)} €)
                  </Button>

                  {/* Change */}
                  {parseFloat(cashReceived) >= total && (
                    <div className="bg-green-900/50 border border-green-700 rounded-lg p-4 text-center">
                      <p className="text-green-400 text-sm">CAMBIO A DEVOLVER</p>
                      <p className="text-3xl font-bold text-green-400">{change.toFixed(2)} €</p>
                    </div>
                  )}
                </div>
              )}

              {paymentMethod === "card" && (
                <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-4 text-center">
                  <CreditCard className="w-12 h-12 text-blue-400 mx-auto mb-2" />
                  <p className="text-blue-400">Procesa el pago en el datáfono</p>
                  <p className="text-blue-400/70 text-sm">y confirma cuando esté completado</p>
                </div>
              )}
            </div>

            <DialogFooter className="gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowPayment(false);
                  setCashReceived("");
                }}
                className="h-9 px-4 py-2 border border-gray-700 text-gray-300 hover:bg-gray-800 rounded-md font-medium"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleProcessOrder}
                disabled={isProcessing || (paymentMethod === "cash" && parseFloat(cashReceived) < total)}
                className="h-9 px-4 py-2 bg-green-500 hover:bg-green-600 text-black font-bold rounded-md inline-flex items-center justify-center disabled:opacity-50 disabled:pointer-events-none"
              >
                {isProcessing ? (
                  "Procesando..."
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    CONFIRMAR PEDIDO
                  </>
                )}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Discount Dialog */}
        <Dialog open={showDiscountDialog} onOpenChange={setShowDiscountDialog}>
          <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-purple-400 flex items-center gap-2">
                <Lock className="w-5 h-5" />
                Aplicar Descuento (Requiere Admin)
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm text-gray-400 block mb-2">Porcentaje de descuento</label>
                <div className="relative">
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    placeholder="10"
                    value={discountPercent}
                    onChange={(e) => setDiscountPercent(e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">%</span>
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-400 block mb-2">Motivo del descuento</label>
                <Input
                  placeholder="Ej: Cliente frecuente, promoción..."
                  value={discountReason}
                  onChange={(e) => setDiscountReason(e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div className="border-t border-gray-700 pt-4">
                <label className="text-sm text-gray-400 block mb-2">
                  🔐 Contraseña del Administrador
                </label>
                <Input
                  type="password"
                  placeholder="Introduce la contraseña del admin"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              {discountPercent && subtotal > 0 && (
                <div className="bg-purple-900/30 border border-purple-700 rounded-lg p-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Subtotal:</span>
                    <span className="text-white">{subtotal.toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-purple-400">Descuento ({discountPercent}%):</span>
                    <span className="text-purple-400">-{((subtotal * parseFloat(discountPercent || 0)) / 100).toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between font-bold border-t border-purple-700 pt-2 mt-2">
                    <span className="text-white">Nuevo total:</span>
                    <span className="text-yellow-500">{(subtotal - (subtotal * parseFloat(discountPercent || 0)) / 100 + deliveryFee).toFixed(2)} €</span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => {
                  setShowDiscountDialog(false);
                  setAdminPassword("");
                  setDiscountPercent("");
                  setDiscountReason("");
                }}
                className="h-9 px-4 py-2 border border-gray-700 text-gray-300 hover:bg-gray-800 rounded-md font-medium"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleVerifyDiscount}
                disabled={isVerifyingDiscount || !discountPercent || !adminPassword}
                className="h-9 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white font-bold rounded-md inline-flex items-center justify-center disabled:opacity-50 disabled:pointer-events-none"
              >
                {isVerifyingDiscount ? (
                  <span className="animate-spin mr-2">⏳</span>
                ) : (
                  <Lock className="w-4 h-4 mr-2" />
                )}
                AUTORIZAR DESCUENTO
              </button>
            </div>
          </DialogContent>
        </Dialog>
        {/* History Detail Dialog with Timeline */}
        <Dialog open={showHistoryDetail} onOpenChange={setShowHistoryDetail}>
          <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0">
            <DialogHeader className="p-4 border-b border-gray-800 flex flex-row items-center justify-between">
              <div>
                <DialogTitle className="text-blue-400 text-xl font-black">
                  DETALLES DEL PEDIDO #{selectedHistoryOrder?.order_id.slice(-6).toUpperCase()}
                </DialogTitle>
                <div className="flex gap-2 mt-1">
                  <Badge variant="outline" className="border-gray-700 text-gray-400 text-[10px]">
                    {new Date(selectedHistoryOrder?.created_at).toLocaleString()}
                  </Badge>
                  <Badge className={selectedHistoryOrder?.payment_status === 'paid' ? 'bg-green-500 text-black' : 'bg-yellow-500 text-black'}>
                    {selectedHistoryOrder?.payment_status === 'paid' ? 'COBRADO' : 'PENDIENTE'}
                  </Badge>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setShowHistoryDetail(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </Button>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto p-4 flex gap-6">
              {/* Info Column */}
              <div className="flex-1 space-y-4">
                <section className="bg-gray-800/50 rounded-lg p-4 border border-gray-800">
                  <h4 className="text-xs font-black text-gray-500 uppercase mb-3 tracking-widest">Resumen de Productos</h4>
                  <div className="space-y-2">
                    {selectedHistoryOrder?.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <div className="flex gap-2">
                          <span className="text-yellow-500 font-bold">{item.quantity}x</span>
                          <span className="text-gray-200">{item.name}</span>
                        </div>
                        <span className="text-white font-mono">{(item.price * item.quantity).toFixed(2)}€</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-3 border-t border-gray-700 flex justify-between font-black text-lg">
                    <span className="text-gray-400">TOTAL</span>
                    <span className="text-yellow-500">{(selectedHistoryOrder?.total || 0).toFixed(2)}€</span>
                  </div>
                </section>

                <section className="bg-gray-800/50 rounded-lg p-4 border border-gray-800">
                  <h4 className="text-xs font-black text-gray-500 uppercase mb-3 tracking-widest">Estado de Impresión</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <p className="text-[10px] text-gray-500 uppercase font-bold">Ticket Cliente</p>
                      <div className="flex items-center gap-2">
                        <Badge className={selectedHistoryOrder?.customer_print_status === 'printed' ? 'bg-green-600' : 'bg-yellow-600'}>
                          {selectedHistoryOrder?.customer_print_status === 'printed' ? 'Impreso' : 'Pendiente/Error'}
                        </Badge>
                        <Button size="icon" variant="outline" className="h-7 w-7 border-gray-700" onClick={() => handleReprint('customer')}>
                          <Printer className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <p className="text-[10px] text-gray-500 uppercase font-bold">Comanda Cocina</p>
                      <div className="flex items-center gap-2">
                        <Badge className={selectedHistoryOrder?.kitchen_print_status === 'printed' ? 'bg-green-600' : 'bg-yellow-600'}>
                          {selectedHistoryOrder?.kitchen_print_status === 'printed' ? 'Impreso' : 'Pendiente'}
                        </Badge>
                        <Button size="icon" variant="outline" className="h-7 w-7 border-gray-700" onClick={() => handleReprint('kitchen')}>
                          <Printer className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </section>
              </div>

              {/* Audit Timeline Column */}
              <div className="w-80 bg-black/20 rounded-lg p-4 border border-gray-800 flex flex-col">
                <h4 className="text-xs font-black text-gray-500 uppercase mb-4 tracking-widest flex items-center gap-2">
                  <Clock className="w-3 h-3" /> Auditoría / Historial
                </h4>
                <div className="flex-1 space-y-6 relative border-l border-gray-800 ml-2 pl-6 overflow-y-auto pr-2">
                  {selectedHistoryOrder?.events?.map((event, idx) => (
                    <div key={idx} className="relative">
                      {/* Dot */}
                      <div className={`absolute -left-[31px] w-2.5 h-2.5 rounded-full border-2 border-gray-900 ${event.type.includes('ok') ? 'bg-green-500' :
                        event.type.includes('failed') ? 'bg-red-500' :
                          event.type.includes('created') ? 'bg-blue-500' :
                            'bg-yellow-500'
                        }`} />

                      <p className="text-[10px] text-gray-500 font-mono">
                        {new Date(event.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </p>
                      <p className="text-xs font-bold text-gray-200 mt-0.5">
                        {event.type.replace(/_/g, ' ').toUpperCase()}
                      </p>
                      {event.actor_role && (
                        <p className="text-[9px] text-blue-400 font-black uppercase mt-0.5">
                          Actor: {event.actor_role}
                        </p>
                      )}
                      {event.meta && Object.keys(event.meta).length > 0 && (
                        <div className="mt-1 bg-white/5 rounded p-1.5 text-[10px] text-gray-400 font-mono">
                          {JSON.stringify(event.meta)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter className="p-4 bg-gray-800/50 border-t border-gray-800">
              <Button variant="outline" className="border-gray-700 text-gray-300" onClick={() => setShowHistoryDetail(false)}>
                Cerrar Detalle
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>


      {/* Menu Selection Dialog */}
      <Dialog 
        open={!!menuSelectionItem} 
        onOpenChange={() => { 
          setMenuSelectionItem(null); 
          setSelectedItemExtras([]); 
        }}
      >
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-center">
              {menuSelectionItem?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 py-4">
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="h-20 flex-1 flex flex-col gap-1 border-gray-700 hover:bg-gray-800 hover:text-white"
                onClick={() => {
                  if (menuSelectionItem?.category_id === 'cat_tacos' || menuSelectionItem?.category_id === 'cat_entrantes') {
                    setSizeSelectionItem({ item: menuSelectionItem, is_menu: false });
                  } else {
                    const itemWithExtras = {
                      ...menuSelectionItem,
                      price: parseFloat(menuSelectionItem.price) + (selectedItemExtras.length * 1.5),
                      extras: selectedItemExtras,
                      notes: selectedItemExtras.length > 0 ? `EXTRAS: ${selectedItemExtras.join(', ')}` : ''
                    };
                    processAddToCart(itemWithExtras, false);
                  }
                  setMenuSelectionItem(null);
                  setSelectedItemExtras([]);
                }}
              >
                <span className="text-lg font-bold">SOLO</span>
                <span className="text-sm text-gray-400">{(parseFloat(menuSelectionItem?.price || 0) + (selectedItemExtras.length * (customTacoConfig?.extra_item_price || 1.5))).toFixed(2)} €</span>
              </Button>
              <Button
                className="h-20 flex-1 flex flex-col gap-1 bg-yellow-500 hover:bg-yellow-400 text-black shadow-lg"
                onClick={() => {
                  const basePrice = parseFloat(menuSelectionItem?.price || 0);
                  const lname = (menuSelectionItem?.name || "").toLowerCase();
                  const isBk = menuSelectionItem?.category_id === 'cat_burger' || 
                              menuSelectionItem?.category_id === 'cat_kebab' || 
                              menuSelectionItem?.category_id === 'cat_ztacos' || 
                              lname.includes('burger') || 
                              lname.includes('kebab') || 
                              lname.includes('zbox') || 
                              lname.includes('zdurum') ||
                              lname.includes('(zt') || 
                              lname.includes('ztaco') || 
                              lname.includes('z taco') || 
                              lname.startsWith('zb') || 
                              lname.startsWith('zk') || 
                              lname.startsWith('zt') || 
                              lname.startsWith('zx');
                  
                  // Fix: Ensure menuAdd is at least 3.0 if no valid menu_price is set
                  const diff = parseFloat(menuSelectionItem?.menu_price || 0) - basePrice;
                  const menuAdd = (diff > 0.1) ? diff : (customTacoConfig?.menu_surcharge || 3.0);
                  const finalMenuPrice = basePrice + menuAdd;

                  if (menuSelectionItem?.category_id === 'cat_tacos' || menuSelectionItem?.category_id === 'cat_entrantes' || menuSelectionItem?.category_id === 'cat_ztacos') {
                    setSizeSelectionItem({ item: menuSelectionItem, is_menu: true });
                  } else {
                    const itemWithExtras = {
                      ...menuSelectionItem,
                      menu_price: finalMenuPrice + (selectedItemExtras.length * 1.5),
                      extras: selectedItemExtras,
                      notes: selectedItemExtras.length > 0 ? `EXTRAS: ${selectedItemExtras.join(', ')}` : ''
                    };
                    processAddToCart(itemWithExtras, true);
                  }
                  setMenuSelectionItem(null);
                  setSelectedItemExtras([]);
                }}
              >
                <div className="flex items-center gap-2">
                  <UtensilsCrossed className="w-5 h-5" />
                  <span className="text-xl font-black">MENU</span>
                </div>
                <span className="text-lg font-black">{(() => {
                  const basePrice = parseFloat(menuSelectionItem?.price || 0);
                  const lname = (menuSelectionItem?.name || "").toLowerCase();
                  const isBk = menuSelectionItem?.category_id === 'cat_burger' || 
                              menuSelectionItem?.category_id === 'cat_kebab' || 
                              menuSelectionItem?.category_id === 'cat_ztacos' || 
                              lname.includes('burger') || 
                              lname.includes('kebab') || 
                              lname.includes('zbox') || 
                              lname.includes('zdurum') ||
                              lname.includes('(zt') || 
                              lname.includes('ztaco') || 
                              lname.includes('z taco') || 
                              lname.startsWith('zb') || 
                              lname.startsWith('zk') || 
                              lname.startsWith('zt') || 
                              lname.startsWith('zx');
                  
                  const diff = parseFloat(menuSelectionItem?.menu_price || 0) - basePrice;
                  const menuAdd = (diff > 0.1) ? diff : (customTacoConfig?.menu_surcharge || 3.0);
                  return (basePrice + menuAdd + (selectedItemExtras.length * (customTacoConfig?.extra_item_price || 1.5))).toFixed(2);
                })()} €</span>
              </Button>
            </div>

            {/* Extras Section for Burger / Kebab */}
            {(() => {
              const lnameForExtras = (menuSelectionItem?.name || "").toLowerCase();
              const isBKForExtras = 
                menuSelectionItem?.category_id === 'cat_burger' || 
                menuSelectionItem?.category_id === 'cat_kebab' ||
                menuSelectionItem?.category_id === 'cat_ztacos' || 
                lnameForExtras.includes('burger') || 
                lnameForExtras.includes('kebab') || 
                lnameForExtras.includes('zbox') || 
                lnameForExtras.includes('zdurum') ||
                lnameForExtras.includes('(zt') || 
                lnameForExtras.includes('ztaco') || 
                lnameForExtras.includes('z taco') || 
                lnameForExtras.startsWith('zb') || 
                lnameForExtras.startsWith('zk') || 
                lnameForExtras.startsWith('zt') || 
                lnameForExtras.startsWith('zx');
              
              if (!isBKForExtras) return null;

              return (
                <div className="border-t border-gray-800 pt-4 mt-2">
                  <h4 className="text-xs font-black text-yellow-500 uppercase mb-3 flex justify-between">
                    <span>AÑADIR EXTRAS (+{(customTacoConfig?.extra_item_price || 1.5).toFixed(2)}€/u)</span>
                    <Badge variant="outline" className="text-white border-white/20">
                      {selectedItemExtras.length} seleccionados
                    </Badge>
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    {(customTacoConfig?.extras_list?.filter(e => e.active !== false) || [
                      { name: 'EXTRA CARNE', price: 1.5 }, 
                      { name: 'EXTRA QUESO', price: 1.5 }, 
                      { name: 'EXTRA BEICON', price: 1.5 }, 
                      { name: 'EXTRA HUEVO', price: 1.5 }, 
                      { name: 'EXTRA SALSA', price: 1.5 },
                      { name: 'CEBOLLA CAR.', price: 1.5 }, 
                      { name: 'GUACAMOLE', price: 1.5 }
                    ]).map(extra => {
                      const extraName = extra.name || extra;
                      const extraPrice = parseFloat(extra.price || customTacoConfig?.extra_item_price || 1.5);
                      const isSelected = selectedItemExtras.includes(extraName);
                      return (
                        <Button
                          key={extraName}
                          variant="outline"
                          size="sm"
                          className={`h-9 text-[10px] font-bold ${isSelected ? 'bg-yellow-500 text-black border-yellow-500' : 'border-gray-700 text-gray-500'}`}
                          onClick={() => {
                            setSelectedItemExtras(prev => 
                              isSelected ? prev.filter(e => e !== extraName) : [...prev, extraName]
                            );
                          }}
                        >
                          {isSelected && <Plus className="w-3 h-3 mr-1" />}
                          {extraName}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              className="w-full text-gray-500 hover:text-white"
              onClick={() => setMenuSelectionItem(null)}
            >
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Size Selection Dialog para Tacos Predefinidos */}
      {(() => {
        const sizes = customTacoConfig?.tortillas || TACO_OPTIONS.sizes;
        const sizeM = sizes.find(s => s.id === 'M' || s.name === 'M');
        const baseMPrice = sizeM ? parseFloat(sizeM.price) : 5.0;
        const getInc = (sizeId, fallback) => {
           const sOpt = sizes.find(s => s.id === sizeId || s.name === sizeId);
           if (sOpt) return Math.max(0, parseFloat(sOpt.price) - baseMPrice);
           return fallback;
        };
        const adds = { L: getInc('L', 2), XL: getInc('XL', 5), XXL: getInc('XXL', 8) };

        return (
          <Dialog open={!!sizeSelectionItem} onOpenChange={() => { setSizeSelectionItem(null); setSizeDialogGratinado(false); setSizeDialogGratinadoQueso('Mozzarella'); setSizeDialogDough(null); }}>
            <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-sm">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-center">
                  {sizeSelectionItem?.item?.name || 'Selecciona Tamaño'}
                </DialogTitle>
              </DialogHeader>

              {/* Tamaño */}
              <div className="grid grid-cols-2 gap-3 pt-2 pb-1">
                {sizeSelectionItem?.item?.category_id === 'cat_entrantes' ? (
                  <>
                    <Button
                      variant="outline"
                      className="h-20 flex flex-col gap-1 border-gray-700 hover:bg-gray-800 hover:text-white"
                      onClick={() => handleSizeSelection('MEDIA', 0)}
                    >
                      <span className="text-xl font-bold">MEDIA</span>
                      <span className="text-xs text-gray-400">Ración</span>
                    </Button>
                    <Button
                      variant="outline"
                      className="h-20 flex flex-col gap-1 border-gray-700 hover:bg-gray-800 hover:text-white"
                      onClick={() => {
                        const enteraPrice = sizeSelectionItem.item.entera_price || (parseFloat(sizeSelectionItem.item.price) + 3.5);
                        const diff = enteraPrice - parseFloat(sizeSelectionItem.item.price);
                        handleSizeSelection('ENTERA', diff);
                      }}
                    >
                      <span className="text-xl font-bold">ENTERA</span>
                      <span className="text-xs text-yellow-500">Ración Completa</span>
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      className={`h-20 flex flex-col gap-1 border-gray-700 hover:bg-gray-800 hover:text-white transition-all ${sizeDialogDough ? 'border-yellow-500 shadow-[0_0_10px_rgba(234,179,8,0.2)]' : ''}`}
                      onClick={() => handleSizeSelection('M', 0, sizeDialogGratinado, sizeDialogGratinadoQueso, sizeDialogDough)}
                    >
                      <span className="text-xl font-bold">M</span>
                      <span className="text-xs text-gray-400">Precio base</span>
                      {sizeDialogGratinado && <span className="text-[9px] text-yellow-400">+2€ grat.</span>}
                      {sizeDialogDough && <span className="text-[9px] text-orange-400">+{customTacoConfig?.dough_price ||'1.50'}€ masa</span>}
                    </Button>
                    <Button
                      variant="outline"
                      className="h-20 flex flex-col gap-1 border-gray-700 hover:bg-gray-800 hover:text-white"
                      onClick={() => handleSizeSelection('L', adds.L, sizeDialogGratinado, sizeDialogGratinadoQueso)}
                    >
                      <span className="text-xl font-bold">L</span>
                      <span className="text-xs text-yellow-500">+ {adds.L.toFixed(2)} €</span>
                      {sizeDialogGratinado && <span className="text-[9px] text-yellow-400">+2€ grat.</span>}
                    </Button>
                    <Button
                      variant="outline"
                      className="h-20 flex flex-col gap-1 border-gray-700 hover:bg-gray-800 hover:text-white"
                      onClick={() => handleSizeSelection('XL', adds.XL, sizeDialogGratinado, sizeDialogGratinadoQueso)}
                    >
                      <span className="text-xl font-bold">XL</span>
                      <span className="text-xs text-yellow-500">+ {adds.XL.toFixed(2)} €</span>
                      {sizeDialogGratinado && <span className="text-[9px] text-yellow-400">+2€ grat.</span>}
                    </Button>
                    <Button
                      variant="outline"
                      className="h-20 flex flex-col gap-1 border-gray-700 hover:bg-gray-800 hover:text-white"
                      onClick={() => handleSizeSelection('XXL', adds.XXL, sizeDialogGratinado, sizeDialogGratinadoQueso)}
                    >
                      <span className="text-xl font-bold">XXL</span>
                      <span className="text-xs text-yellow-500">+ {adds.XXL.toFixed(2)} €</span>
                      {sizeDialogGratinado && <span className="text-[9px] text-yellow-400">+2€ grat.</span>}
                    </Button>
                  </>
                )}
              </div>

              {/* Gratinado */}
              <div className="border-t border-gray-800 pt-3 pb-1 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-black text-yellow-500 uppercase tracking-tight">¿GRATINADO? (+{(customTacoConfig?.gratinado_price || 2.0).toFixed(2)} €)</span>
                  <Switch
                    checked={sizeDialogGratinado}
                    onCheckedChange={setSizeDialogGratinado}
                  />
                </div>
                {sizeDialogGratinado && (
                  <div className="grid grid-cols-4 gap-1">
                    {TACO_OPTIONS.gratinados.map(q => (
                      <Button
                        key={q}
                        size="sm"
                        variant={sizeDialogGratinadoQueso === q ? 'default' : 'outline'}
                        className={`h-8 text-[10px] ${sizeDialogGratinadoQueso === q ? 'bg-yellow-600 text-white' : 'border-gray-700 text-gray-400'}`}
                        onClick={() => setSizeDialogGratinadoQueso(q)}
                      >
                        {q}
                      </Button>
                    ))}
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button
                  variant="ghost"
                  className="w-full text-gray-500 hover:text-white"
                  onClick={() => { setSizeSelectionItem(null); setSizeDialogGratinado(false); setSizeDialogGratinadoQueso('Mozzarella'); }}
                >
                  Cancelar
                </Button>
              </DialogFooter>

              {/* Masa Selection (Only for M) */}
              {(!sizeSelectionItem?.item?.category_id || sizeSelectionItem?.item?.category_id === 'cat_tacos' || sizeSelectionItem?.item?.category_id === 'cat_ztacos') && (
                <div className="border-t border-gray-800 pt-3 pb-3 space-y-2 px-4 bg-black/20 rounded-b-lg">
                  <div className="flex items-center justify-between pointer-events-none opacity-50">
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Opción Premium (Solo M)</span>
                  </div>
                  <h4 className="text-xs font-black text-white uppercase tracking-tight flex items-center gap-2">
                    ¿SELECCIÓN DE MASA? (+{customTacoConfig?.dough_price || '1.50'} €)
                  </h4>
                  <div className="grid grid-cols-4 gap-1">
                    {(customTacoConfig?.dough_options || TACO_OPTIONS.doughs.map(name => ({name, price: 1.5}))).map(d => {
                      const name = typeof d === 'string' ? d : d.name;
                      const isSelected = sizeDialogDough === name;
                      return (
                        <Button
                          key={name}
                          size="sm"
                          variant={isSelected ? 'default' : 'outline'}
                          className={`h-8 text-[10px] transition-all font-bold ${isSelected ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/20' : 'border-gray-700 text-gray-400 hover:text-white'}`}
                          onClick={() => setSizeDialogDough(isSelected ? null : name)}
                        >
                          {name}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        );
      })()}


      {/* Taco Builder Modal */}
      <Dialog open={showTacoBuilder} onOpenChange={setShowTacoBuilder}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700">
          <DialogHeader className="border-b border-gray-800 pb-4">
            <DialogTitle className="text-2xl font-black text-yellow-500 flex items-center gap-2 italic">
              <UtensilsCrossed className="w-6 h-6" /> CREA TU ZTACO PREFERIDO
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Step 1: Tamaño / Tortilla */}
            <section>
              <h4 className="text-sm font-black text-gray-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                <span className="bg-white text-black w-5 h-5 rounded-full flex items-center justify-center text-[10px]">1</span> TAMAÑO
              </h4>
              <div className="grid grid-cols-4 gap-2">
                {(customTacoConfig?.tortillas || TACO_OPTIONS.sizes).map(opt => {
                  const id = opt.id || opt.name;
                  const name = opt.name;
                  const increment = parseFloat(opt.price) || 0;
                  // When customTacoConfig exists, prices are INCREMENTS → add base_price for absolute display
                  const cfgBase = customTacoConfig ? (parseFloat(customTacoConfig.base_price) || 0) : 0;
                  const displayPrice = cfgBase + increment; // absolute price shown on button
                  const isSelected = tacoConfig.size === id || tacoConfig.size === name;

                  return (
                    <Button
                      key={id}
                      variant={isSelected ? "default" : "outline"}
                      className={isSelected ? "bg-yellow-500 text-black border-yellow-500" : "border-gray-700"}
                      onClick={() => setTacoConfig(prev => ({ ...prev, size: name, dough: name === 'M' ? prev.dough : null }))}
                    >
                      <div className="flex flex-col">
                        <span className="text-lg font-black">{name}</span>
                        <span className="text-[10px] opacity-70">{displayPrice.toFixed(2)}€</span>
                      </div>
                    </Button>
                  );
                })}
              </div>
            </section>
            
            {/* NEW: Step 1.5: Masa (Only for M) */}
            {tacoConfig.size === 'M' && (
              <section className="bg-black/20 p-4 rounded-xl border border-gray-800 animate-in fade-in slide-in-from-top-4">
                <h4 className="text-xs font-black text-orange-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                   ¿SELECCIÓN DE MASA? (+{customTacoConfig?.dough_price ||'1.50'}€)
                </h4>
                <div className="grid grid-cols-4 gap-2">
                    {(customTacoConfig?.dough_options || TACO_OPTIONS.doughs.map(name => ({name, price: 1.5}))).map(d => {
                      const name = typeof d === 'string' ? d : d.name;
                      const isSelected = tacoConfig.dough === name;
                      return (
                        <Button
                          key={name}
                          size="sm"
                          variant={isSelected ? "default" : "outline"}
                          className={`h-9 flex flex-col items-center justify-center ${isSelected ? "bg-orange-500 text-white border-orange-500" : "border-gray-800 text-gray-400"}`}
                          onClick={() => setTacoConfig(prev => ({ ...prev, dough: isSelected ? null : name }))}
                        >
                          <span className="text-[10px] leading-tight font-black">{name}</span>
                        </Button>
                      );
                    })}
                </div>
              </section>
            )}

            {/* Step 2: Salsas */}
            <section>
              <h4 className="text-sm font-black text-gray-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                <span className="bg-white text-black w-5 h-5 rounded-full flex items-center justify-center text-[10px]">2</span> SALSAS
                <span className="text-[9px] font-normal lowercase opacity-60 ml-2">(Max 2 gratis)</span>
              </h4>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {(customTacoConfig?.salsas || TACO_OPTIONS.sauces).map(sauce => {
                  const name = typeof sauce === 'string' ? sauce : sauce.name;
                  const isSelected = tacoConfig.sauces.includes(name);
                  return (
                    <Button
                      key={name}
                      size="sm"
                      variant={isSelected ? "default" : "outline"}
                      className={`h-9 text-[11px] ${isSelected ? "bg-orange-500 text-white border-orange-500" : "border-gray-800 text-gray-400"}`}
                      onClick={() => {
                        setTacoConfig(prev => {
                          const newSauces = isSelected
                            ? prev.sauces.filter(x => x !== name)
                            : [...prev.sauces, name];
                          return { ...prev, sauces: newSauces };
                        });
                      }}
                    >
                      {name}
                    </Button>
                  );
                })}
              </div>
            </section>

            {/* Step 3: Carnes */}
            <section>
              <h4 className="text-sm font-black text-gray-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                <span className="bg-white text-black w-5 h-5 rounded-full flex items-center justify-center text-[10px]">3</span> CARNES
                <span className="text-[9px] font-normal lowercase opacity-60 ml-2">({customTacoConfig?.max_proteins || 1} carnes gratis)</span>
              </h4>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {(customTacoConfig?.proteins || TACO_OPTIONS.meats).map(protein => {
                  const name = typeof protein === 'string' ? protein : protein.name;
                  const isSelected = tacoConfig.meats.includes(name);
                  return (
                    <Button
                      key={name}
                      size="sm"
                      variant={isSelected ? "default" : "outline"}
                      className={`h-9 text-[11px] ${isSelected ? "bg-red-500 text-white border-red-500" : "border-gray-800 text-gray-400"}`}
                      onClick={() => {
                        setTacoConfig(prev => {
                          const newMeats = isSelected
                            ? prev.meats.filter(x => x !== name)
                            : [...prev.meats, name];
                          return { ...prev, meats: newMeats };
                        });
                      }}
                    >
                      {name}
                    </Button>
                  );
                })}
              </div>
            </section>

            {/* Step 4: Extras */}
            <section>
              <h4 className="text-sm font-black text-gray-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                <span className="bg-white text-black w-5 h-5 rounded-full flex items-center justify-center text-[10px]">4</span> EXTRAS
                <span className="text-[9px] font-normal lowercase opacity-60 ml-2">(Precios según Admin)</span>
              </h4>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {(customTacoConfig?.toppings || TACO_OPTIONS.extras).map(extra => {
                  const name = typeof extra === 'string' ? extra : extra.name;
                  const isSelected = tacoConfig.extras.includes(name);
                  const price = typeof extra === 'string' ? 1.5 : (extra.price || 0);

                  return (
                    <Button
                      key={name}
                      size="sm"
                      variant={isSelected ? "default" : "outline"}
                      className={`h-9 flex flex-col items-center justify-center ${isSelected ? "bg-blue-500 text-white border-blue-500" : "border-gray-800 text-gray-400"}`}
                      onClick={() => {
                        setTacoConfig(prev => {
                          const newExtras = isSelected
                            ? prev.extras.filter(x => x !== name)
                            : [...prev.extras, name];
                          return { ...prev, extras: newExtras };
                        });
                      }}
                    >
                      <span className="text-[10px] leading-tight">{name}</span>
                      {price > 0 && <span className="text-[8px] opacity-60">+{price}€</span>}
                    </Button>
                  );
                })}
              </div>
            </section>

            {/* Gratinated & Menu */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-800">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-black text-yellow-500 uppercase tracking-tighter">¿GRATINADO? (+2€)</h4>
                  <Switch
                    checked={tacoConfig.isGratinado}
                    onCheckedChange={(val) => setTacoConfig(prev => ({ ...prev, isGratinado: val }))}
                  />
                </div>
                {tacoConfig.isGratinado && (
                  <div className="grid grid-cols-2 gap-1">
                    {TACO_OPTIONS.gratinados.map(q => (
                      <Button
                        key={q}
                        size="sm"
                        variant={tacoConfig.gratinadoQueso === q ? "default" : "outline"}
                        className={`h-7 text-[10px] ${tacoConfig.gratinadoQueso === q ? "bg-yellow-600 text-white" : "border-gray-800"}`}
                        onClick={() => setTacoConfig(prev => ({ ...prev, gratinadoQueso: q }))}
                      >
                        {q}
                      </Button>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex flex-col justify-center items-center bg-gray-800/50 rounded-lg p-3 border border-gray-700">
                <h4 className="text-xs font-black text-gray-400 uppercase mb-2">¿BASTÓN / MENÚ? (+3€)</h4>
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] ${!tacoConfig.isMenu ? 'text-white font-bold' : 'text-gray-600'}`}>SOLO</span>
                  <Switch
                    checked={tacoConfig.isMenu}
                    onCheckedChange={(val) => setTacoConfig(prev => ({ ...prev, isMenu: val }))}
                  />
                  <span className={`text-[10px] ${tacoConfig.isMenu ? 'text-yellow-500 font-bold' : 'text-gray-600'}`}>TACO MENU</span>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="sticky bottom-0 bg-gray-900 pt-4 border-t border-gray-800 mt-2">
            <div className="w-full flex items-center justify-between gap-4">
              <div className="flex flex-col">
                <span className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">Total Taco</span>
                <span className="text-2xl font-black text-yellow-500">
                  {(() => {
                    const dynamicTacoOptions = customTacoConfig ? {
                      sizes: customTacoConfig.tortillas || [],
                      sauces: customTacoConfig.salsas || [],
                      meats: customTacoConfig.proteins || [],
                      extras: customTacoConfig.toppings || [],
                      baseItem: null
                    } : TACO_OPTIONS;

                    const selectedSize = dynamicTacoOptions.sizes.find(s => (s.id === tacoConfig.size || s.name === tacoConfig.size));
                    // base_price is the M-size absolute price; tortilla prices are increments over it
                    const cfgBasePrice = customTacoConfig ? (parseFloat(customTacoConfig.base_price) || 0) : 0;
                    const base = selectedSize
                      ? cfgBasePrice + parseFloat(selectedSize.price || 0)
                      : (TACO_OPTIONS.sizes.find(s => s.id === tacoConfig.size)?.price || 0);

                    // For dynamic extras, we need to find their actual prices
                    let extrasPrice = 0;
                    tacoConfig.extras.forEach(extraName => {
                      const found = dynamicTacoOptions.extras.find(e => e.name === extraName);
                      extrasPrice += found ? (found.price || 0) : 1.5;
                    });

                    // For dynamic sauces and meats, handle gratis vs extra
                    const extraSauceCount = Math.max(0, tacoConfig.sauces.length - 2);
                    let extraSaucePrice = 0;
                    if (extraSauceCount > 0) {
                      // Just a simple heuristic if we don't have individual prices for all
                      extraSaucePrice = extraSauceCount * 0.5;
                    }

                    const extraMeatCount = Math.max(0, tacoConfig.meats.length - (customTacoConfig?.max_proteins || 1));
                    let extraMeatPrice = 0;
                    if (extraMeatCount > 0) {
                      extraMeatPrice = extraMeatCount * 1.5;
                    }

                    const gratinadoPrice = tacoConfig.isGratinado ? 2 : 0;
                    const doughPrice = (tacoConfig.size === 'M' && tacoConfig.dough) ? (customTacoConfig?.dough_price || 1.50) : 0;
                    const menuPrice = tacoConfig.isMenu ? 3 : 0;

                    const finalTotal = (base + extrasPrice + extraSaucePrice + extraMeatPrice + gratinadoPrice + doughPrice + menuPrice);
                    return finalTotal.toFixed(2);
                  })()} €
                </span>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" className="text-gray-500" onClick={() => setShowTacoBuilder(false)}>
                  Cancelar
                </Button>
                <Button
                  className="bg-green-600 hover:bg-green-500 text-white font-bold px-8"
                  onClick={() => {
                    // FIX 1: Use dynamic customTacoConfig prices, fall back to TACO_OPTIONS
                    const dynamicSizes = customTacoConfig?.tortillas || TACO_OPTIONS.sizes;
                    const dynamicExtras = customTacoConfig?.toppings || [];
                    const price = (() => {
                      const selectedSize = dynamicSizes.find(s => s.id === tacoConfig.size || s.name === tacoConfig.size);
                      // base_price is the M-size absolute price; tortilla prices are increments over it
                      const cfgBasePrice = customTacoConfig ? (parseFloat(customTacoConfig.base_price) || 0) : 0;
                      const b = selectedSize
                        ? cfgBasePrice + parseFloat(selectedSize.price || 0)
                        : (TACO_OPTIONS.sizes.find(s => s.id === tacoConfig.size)?.price || 0);
                      let ex = 0;
                      tacoConfig.extras.forEach(extraName => {
                        const found = dynamicExtras.find(e => e.name === extraName);
                        ex += found ? (parseFloat(found.price) || 0) : 1.5;
                      });
                      const sau = Math.max(0, tacoConfig.sauces.length - 2) * 0.5;
                      const mt = Math.max(0, tacoConfig.meats.length - (customTacoConfig?.max_proteins || 1)) * 1.5;
                      const grat = tacoConfig.isGratinado ? 2 : 0;
                      const dghPrice = (tacoConfig.size === 'M' && tacoConfig.dough) ? (customTacoConfig?.dough_price || 1.50) : 0;
                      const mn = tacoConfig.isMenu ? 3 : 0;
                      return b + ex + sau + mt + grat + dghPrice + mn;
                    })();

                    const description = [
                      `TAMAÑO ${tacoConfig.size}`,
                      tacoConfig.dough ? `MASA ${tacoConfig.dough}` : '',
                      tacoConfig.sauces.length > 0 ? `SALSAS: ${tacoConfig.sauces.join(', ')}` : '',
                      tacoConfig.meats.length > 0 ? `CARNES: ${tacoConfig.meats.join(', ')}` : '',
                      tacoConfig.extras.length > 0 ? `EXTRAS: ${tacoConfig.extras.join(', ')}` : '',
                      tacoConfig.isGratinado ? `GRATINADO: ${tacoConfig.gratinadoQueso}` : '',
                      tacoConfig.isMenu ? 'Taco Menú' : ''
                    ].filter(Boolean).join(' | ');

                    // FIX 2: safe baseItem fallback so item_id is never undefined
                    const safeBase = tacoConfig.baseItem || {};

                    // FIX 3: unique item_id per custom configuration to avoid merging distinct tacos
                    const configKey = `${tacoConfig.size}_${tacoConfig.dough || 'no'}_${tacoConfig.meats.join('-')}_${tacoConfig.sauces.join('-')}_${tacoConfig.extras.join('-')}_${tacoConfig.isGratinado ? tacoConfig.gratinadoQueso : 'no'}_${tacoConfig.isMenu ? 'menu' : 'solo'}`;
                    const uniqueItemId = `custom_taco_${configKey}`;

                    const finalItem = {
                      ...safeBase,
                      item_id: uniqueItemId,
                      name: `${tacoConfig.meats.length > 1 ? 'MIXTO ' : ''}ZTACO ${tacoConfig.size}${tacoConfig.isMenu ? ' (MENU)' : ''}`,
                      price: price,
                      notes: description,
                      is_menu: tacoConfig.isMenu,
                      is_custom_taco: true
                    };

                    processAddToCart(finalItem, tacoConfig.isMenu);
                    setShowTacoBuilder(false);
                  }}
                >
                  <ShoppingCart className="w-4 h-4 mr-2" /> AÑADIR AL CARRITO
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Printer Settings Dialog */}
      <Dialog open={showPrintSettings} onOpenChange={setShowPrintSettings}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-yellow-500 flex items-center gap-2">
              <Printer className="w-5 h-5" />
              Configuración de Impresora
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">

            {/* Agent Info */}
            <div className="p-4 bg-green-900/30 border border-green-700 rounded-xl">
              <h4 className="font-bold text-green-400 flex items-center gap-2 mb-2">
                <Check className="w-4 h-4" />
                Agente Local Activo
              </h4>
              <p className="text-xs text-gray-300">
                La impresión es gestionada automáticamente por el <strong>ZT-PrintAgent</strong>
                &nbsp;(zt_print_agent.py) que corre en este PC.
              </p>
              <p className="text-xs text-gray-400 mt-2">
                El agente consulta el backend cada 2 segundos y envía los trabajos a la impresora por USB.
                No es necesario configurar nada aquí.
              </p>
            </div>

            {/* Instructions */}
            <div className="p-4 bg-gray-800 rounded-xl border border-gray-700">
              <h4 className="font-bold text-white mb-2 text-sm">¿El agente no está corriendo?</h4>
              <ol className="text-xs text-gray-400 space-y-1 list-decimal list-inside">
                <li>Abre la carpeta del sistema ZTac</li>
                <li>Edita <code className="text-yellow-400">zt_agent.env</code> con los datos correctos</li>
                <li>Ejecuta <code className="text-yellow-400">INICIAR_AGENTE_IMPRESION.bat</code></li>
                <li>Para arranque automático: ejecuta <code className="text-yellow-400">INSTALAR_AGENTE_WINDOWS.bat</code> como Administrador</li>
              </ol>
            </div>

          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout >
  );
}
